import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Menu, X, ShoppingCart, Check, Leaf, Truck, AlertCircle, Shield, 
  Minus, Plus, Trash2, Package, Award, Search, Info, Star, Mail, 
  HelpCircle, ChevronDown, ChevronUp, Clock, Globe, 
  ChevronRight, ChevronLeft, Grid, Copy, ExternalLink, AlertTriangle,
  Zap, MessageCircle, Send, ArrowUp, ArrowRight, MapPin, RefreshCw, CreditCard, Wallet,
  ArrowLeft, Share2, Tag
} from 'lucide-react';
import { GoogleGenAI, Chat, GenerateContentResponse } from "@google/genai";
import { send as sendEmail } from '@emailjs/browser';
import { Strain, CartItem, SortOption, ViewMode, ShippingMethod } from './types';

// ==========================================
// 🚀 LAUNCH CONFIGURATION (PASTE KEYS HERE)
// ==========================================
const EMAILJS_SERVICE_ID = "service_lne574h"; 
const EMAILJS_TEMPLATE_ID = "template_wgertsb"; // <--- PASTE YOUR TEMPLATE ID
const EMAILJS_PUBLIC_KEY = "-29sZ5NSKbnQJPbt7";   // <--- PASTE YOUR PUBLIC KEY

// CONSTANTS
const CART_STORAGE_KEY = 'clones_direct_cart';
const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1603909223429-69bb7101f420?q=80&w=800&auto=format&fit=crop";
const POLYGON_WALLET_ADDRESS = "0x10dc96c6fa9e4cf31b81f70e61d8c1073f1bbfe5";

const categories = ['All', 'Hybrid', 'Indica', 'Sativa', 'Legacy Cuts'];

const strainsData: Strain[] = [
    {
        id: 1,
        name: "Apple Fritter",
        category: "Hybrid",
        lineage: "Sour Apple x Animal Cookies",
        thc: "Hemp",
        stock: 100,
        flowerTime: "8-9 wks",
        terps: "Sweet, Earthy, Apple",
        breeder: "Lump's Flowers",
        imageUrl: "https://iili.io/3qJnedN.jpg",
        description: "Lumpy's cut. A sweet and earthy strain with relaxing effects. Known for its apple-like aroma and flavor."
    },
    {
        id: 2,
        name: "Sherb Cake",
        category: "Hybrid",
        lineage: "Sunset Sherbert x Wedding Cake",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Sweet, Dessert, Berry",
        breeder: "Seed Junky Genetics",
        imageUrl: "https://iili.io/3qJ78Fe.jpg",
        description: "A strain with a sweet, dessert-like flavor and relaxing effects. Known for its dense, frosty buds."
    },
    {
        id: 3,
        name: "Blumosa",
        category: "Sativa",
        lineage: "Blue Cookies x Mimosa",
        thc: "Hemp",
        stock: 100,
        flowerTime: "8 wks",
        terps: "Citrus, Orange, Mimosa",
        breeder: "Mosca",
        imageUrl: "https://iili.io/3qJuWXt.jpg",
        description: "A hybrid strain with a citrusy aroma and uplifting effects. Often used for daytime relief."
    },
    {
        id: 4,
        name: "Blue Dream",
        category: "Sativa",
        lineage: "Blueberry x Haze",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9-10 wks",
        terps: "Berry, Sweet, Haze",
        breeder: "Santa Cruz Cut",
        imageUrl: "https://iili.io/3q3U4BR.png",
        special: true,
        description: "Santa Cruz cut. A popular strain with a sweet berry aroma. Known for its balanced effects and creative inspiration."
    },
    {
        id: 5,
        name: "Bubba Kush",
        category: "Indica",
        lineage: "OG Kush x Unknown (Pre-98)",
        thc: "Hemp",
        stock: 100,
        flowerTime: "8-9 wks",
        terps: "Coffee, Earth, Chocolate",
        breeder: "Pre-98 Cut",
        imageUrl: "https://iili.io/3q3gm91.jpg",
        special: true,
        description: "Pre-98. A heavy strain with a coffee-like aroma and deeply relaxing effects. Ideal for evening use."
    },
    {
        id: 6,
        name: "Bubblegum Cookies",
        category: "Hybrid",
        lineage: "Bubblegum x GSC",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Bubblegum, Sweet, Dough",
        breeder: "Archive",
        imageUrl: "https://iili.io/3q36M8B.png",
        description: "A strain with a sweet, bubblegum flavor and relaxing effects. Known for its dense buds."
    },
    {
        id: 7,
        name: "Candy Rain",
        category: "Hybrid",
        lineage: "Gelato x London Pound Cake",
        thc: "Hemp",
        stock: 100,
        flowerTime: "8-9 wks",
        terps: "Candy, Fruity, Sweet",
        breeder: "Cookies Fam",
        imageUrl: "https://iili.io/3fSlD92.png",
        description: "A strain with a sweet, candy-like flavor and balanced effects. Often used for stress relief."
    },
    {
        id: 8,
        name: "Cinderella's Dream",
        category: "Sativa",
        lineage: "Cinex x Blue Dream",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9-10 wks",
        terps: "Citrus, Pine, Haze",
        breeder: "@reigncity_",
        imageUrl: "https://iili.io/3q3PZWg.jpg",
        description: "Breeder cut. A strain with uplifting effects and a citrusy aroma. Known for its creative boost."
    },
    {
        id: 10,
        name: "Gelato Cake",
        category: "Indica",
        lineage: "Gelato x Wedding Cake",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Dessert, Vanilla, Gas",
        breeder: "Unknown",
        imageUrl: "https://iili.io/3q3igQn.jpg",
        description: "A strain with a sweet, dessert-like flavor and relaxing effects. Ideal for evening use."
    },
    {
        id: 11,
        name: "Georgia Pie",
        category: "Hybrid",
        lineage: "Gelatti x Kush Mints",
        thc: "Hemp",
        stock: 100,
        flowerTime: "8-9 wks",
        terps: "Peach, Cobbler, Gas",
        breeder: "Seed Junky Genetics",
        imageUrl: "https://iili.io/3qFdP71.jpg",
        description: "A strain with a sweet, pie-like flavor and balanced effects. Known for its dense buds."
    },
    {
        id: 12,
        name: "GG4",
        category: "Hybrid",
        lineage: "Chem's Sister x Sour Dubb",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Glue, Gas, Earth",
        breeder: "GG Strains",
        imageUrl: "https://iili.io/3qF3mJI.png",
        special: true,
        description: "Original Glue (Josey's cut). A potent strain with a pungent, earthy aroma and relaxing effects. Known for its sticky buds."
    },
    {
        id: 13,
        name: "GMO",
        category: "Indica",
        lineage: "Chemdog x GSC",
        thc: "Hemp",
        stock: 100,
        flowerTime: "10-11 wks",
        terps: "Garlic, Mushroom, Onion",
        breeder: "Mamiko",
        imageUrl: "https://iili.io/3qFqvhF.png",
        special: true,
        description: "SkunkMasterFlex cut. A potent strain with a pungent, garlic-like aroma and relaxing effects. Ideal for experienced users."
    },
    {
        id: 15,
        name: "Orange Push Pop",
        category: "Sativa",
        lineage: "Triangle Kush x Orange Cookies",
        thc: "Hemp",
        stock: 100,
        flowerTime: "8-9 wks",
        terps: "Orange, Cream, Citrus",
        breeder: "Seed Junky Genetics",
        imageUrl: "https://iili.io/3qFq1I9.jpg",
        description: "@reigncity_ selection. A strain with a citrusy aroma and uplifting effects. Ideal for daytime use."
    },
    {
        id: 16,
        name: "Pineapple Haze",
        category: "Sativa",
        lineage: "Pineapple x Haze",
        thc: "Hemp",
        stock: 100,
        flowerTime: "10+ wks",
        terps: "Pineapple, Tropical, Haze",
        breeder: "Barney's Farm",
        imageUrl: PLACEHOLDER_IMG,
        description: "A strain with a tropical, pineapple flavor and energizing effects. Known for its long flowering time."
    },
    {
        id: 17,
        name: "Platinum Garlic",
        category: "Indica",
        lineage: "GMO Cookies x Platinum",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Garlic, Earth, Spice",
        breeder: "InHouse",
        imageUrl: "https://freeimage.host/i/3fU0BII",
        description: "A strain with a pungent, garlic-like aroma and relaxing effects. Known for its dense buds."
    },
    {
        id: 22,
        name: "Triangle Kush",
        category: "Indica",
        lineage: "Original Florida Cut",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9-10 wks",
        terps: "Earth, Fuel, Lemon",
        breeder: "Unknown",
        imageUrl: "https://iili.io/3qFuGQ2.jpg",
        special: true,
        description: "A strain with an earthy, kush-like aroma and deeply relaxing effects. Ideal for evening use."
    },
    {
        id: 23,
        name: "Trinity",
        category: "Sativa",
        lineage: "76 Afghan x Northern Cali",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Skunk, Pine, Earth",
        breeder: "Legacy Cut",
        imageUrl: PLACEHOLDER_IMG,
        special: true,
        description: "A strain with a complex flavor profile and balanced effects. Known for its high potency."
    },
    {
        id: 24,
        name: "Trop Cherries",
        category: "Sativa",
        lineage: "Tropicana Cookies x Cherry",
        thc: "Hemp",
        stock: 100,
        flowerTime: "8-9 wks",
        terps: "Cherry, Citrus, Sweet",
        breeder: "Relentless Genetics",
        imageUrl: "https://iili.io/3qFRizv.jpg",
        description: "Breeder cut. A strain with a sweet, cherry-like flavor and uplifting effects. Ideal for social settings."
    },
    {
        id: 25,
        name: "White Truffles",
        category: "Indica",
        lineage: "Peanut Butter Breath x GG4",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Butter, Earth, Nutty",
        breeder: "Fresh Coast",
        imageUrl: "https://iili.io/3qFafrN.png",
        description: "A strain with an earthy, truffle-like aroma and relaxing effects. Known for its dense buds."
    },
    {
        id: 26,
        name: "Zookies",
        category: "Hybrid",
        lineage: "Animal Cookies x GG4",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Cookie, Diesel, Earth",
        breeder: "Alien Labs",
        imageUrl: "https://iili.io/3qFa63N.png",
        description: "Breeder cut. A strain with a sweet, nutty flavor and balanced effects. Known for its relaxing properties."
    },
    {
        id: 27,
        name: "Ghost OG",
        category: "Indica",
        lineage: "OG Kush Phenotype",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Pine, Earth, Citrus",
        breeder: "OrgnKid",
        imageUrl: PLACEHOLDER_IMG,
        special: true,
        description: "A legendary cut of OG Kush. Known for its potent, relaxing effects and classic earthy pine aroma."
    },
    {
        id: 28,
        name: "Tahoe OG",
        category: "Indica",
        lineage: "OG Kush Phenotype",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Lemon, Earth, Fuel",
        breeder: "Ganja Guru",
        imageUrl: PLACEHOLDER_IMG,
        special: true,
        description: "Strong, fast-acting indica. Perfect for rainy days or late nights. Classic lemon-fuel funk."
    },
    {
        id: 29,
        name: "Black Cherry Gelato",
        category: "Hybrid",
        lineage: "Acai x Black Cherry Funk",
        thc: "Hemp",
        stock: 100,
        flowerTime: "9 wks",
        terps: "Berry, Sweet, Earthy",
        breeder: "Backpackboyz",
        imageUrl: PLACEHOLDER_IMG,
        description: "A beautiful strain with dark purple hues and a sweet, cherry-like aroma. Balanced hybrid effects."
    }
];

// --- Helper Components & Functions ---

// Hook to lock body scroll
const useLockBodyScroll = (isLocked: boolean) => {
    useEffect(() => {
        const originalStyle = window.getComputedStyle(document.body).overflow;
        if (isLocked) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = originalStyle;
        };
    }, [isLocked]);
};

const TermsContent = () => (
    <div className="space-y-6 text-gray-300 leading-relaxed">
        <p className="text-sm text-gray-500">Last Updated: {new Date().toLocaleDateString()}</p>
        
        <section>
            <h3 className="text-xl font-bold text-white mb-2">1. Agreement to Terms</h3>
            <p>By accessing Clones Direct, you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use our services.</p>
        </section>

        <section>
            <h3 className="text-xl font-bold text-white mb-2">2. Eligibility</h3>
            <p>You must be at least 21 years of age to access this site and purchase products. By using our site, you represent and warrant that you meet this age requirement.</p>
        </section>

        <section>
            <h3 className="text-xl font-bold text-white mb-2">3. Legal Compliance</h3>
            <p>All products sold on this site are industrial hemp-derived clones containing less than 0.3% Delta-9 THC, in compliance with the Federal Agriculture Improvement Act of 2018 (The 2018 Farm Bill). You are responsible for ensuring compliance with your local state and municipal laws regarding the possession and cultivation of hemp.</p>
        </section>

        <section>
            <h3 className="text-xl font-bold text-white mb-2">4. Live Plant Guarantee</h3>
            <p>We guarantee that your clones will arrive healthy and viable. If your plants arrive damaged or unhealthy, you must contact us within 7 days of delivery with photographic evidence. We will issue a one-time replacement for any valid claims. We do not offer cash refunds.</p>
        </section>

        <section>
            <h3 className="text-xl font-bold text-white mb-2">5. Shipping Policy</h3>
            <p>We ship via priority express carriers. While we take every precaution to package plants securely in climate-controlled packaging, we are not responsible for carrier delays, lost packages, or weather-related damages once the package has been handed to the carrier, except where covered by our Live Plant Guarantee.</p>
        </section>
        
        <section>
            <h3 className="text-xl font-bold text-white mb-2">6. All Sales Final</h3>
            <p>Due to the nature of live plants, all sales are final. We do not accept returns. Issues will be resolved via replacements only.</p>
        </section>
    </div>
);

const PrivacyContent = () => (
    <div className="space-y-6 text-gray-300 leading-relaxed">
        <section>
            <h3 className="text-xl font-bold text-white mb-2">1. Information We Collect</h3>
            <p>We collect information necessary to fulfill your orders, including your name, shipping address, and email address. We do not store credit card or financial information; payments are processed securely via third-party providers or direct cryptocurrency transfer.</p>
        </section>

        <section>
            <h3 className="text-xl font-bold text-white mb-2">2. How We Use Your Information</h3>
            <ul className="list-disc pl-5 space-y-2">
                <li>To process and ship your orders.</li>
                <li>To communicate with you regarding your order status.</li>
                <li>To send periodic updates or newsletters (only if you opt-in).</li>
            </ul>
        </section>

        <section>
            <h3 className="text-xl font-bold text-white mb-2">3. Information Sharing</h3>
            <p>We respect your privacy. We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties, except for trusted third parties who assist us in operating our website and conducting our business (e.g., shipping carriers), so long as those parties agree to keep this information confidential.</p>
        </section>

        <section>
            <h3 className="text-xl font-bold text-white mb-2">4. Data Security</h3>
            <p>We implement a variety of security measures to maintain the safety of your personal information. Our website is encrypted via Secure Socket Layer (SSL) technology.</p>
        </section>
    </div>
);

const LegalPage: React.FC<{ title: string, content: React.ReactNode, onBack: () => void }> = ({ title, content, onBack }) => (
    <div className="pt-32 pb-20 px-4 md:px-8 max-w-4xl mx-auto min-h-screen animate-fade-in-up">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-green-500 transition mb-8 font-bold uppercase text-xs tracking-widest group">
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Back to Home
        </button>
        <h1 className="text-4xl md:text-6xl font-black text-white mb-8 tracking-tighter border-b border-zinc-800 pb-8">{title}</h1>
        <div className="prose prose-invert prose-lg max-w-none">
            {content}
        </div>
    </div>
);

const getCategoryColor = (cat: string) => {
    if (cat === 'Indica') return { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20', fill: 'text-purple-500' };
    if (cat === 'Sativa') return { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20', fill: 'text-orange-500' };
    return { text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', fill: 'text-green-500' };
};

const Logo = () => {
    const [error, setError] = useState(false);
    
    // Fallback text logo if SVG fails to load
    if (error) return (
        <div className="flex items-center gap-1 py-1">
            <span className="text-xl md:text-2xl font-black text-white tracking-tighter">CLONES</span>
            <span className="text-xl md:text-2xl font-black text-green-500 tracking-tighter">DIRECT</span>
        </div>
    );

    return (
        <img 
            src="/logo.svg" 
            onError={() => setError(true)} 
            alt="Clones Direct" 
            className="h-8 md:h-10 w-auto object-contain" 
        />
    );
};

const ImageWithSkeleton: React.FC<{ src: string, alt: string, className?: string }> = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {(!loaded && !error) && <div className="absolute inset-0 bg-zinc-800 animate-pulse" />}
      <img 
        src={error ? PLACEHOLDER_IMG : src} 
        alt={alt} 
        className={`w-full h-full object-cover transition-opacity duration-700 ease-in-out ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => { setError(true); setLoaded(true); }}
      />
    </div>
  );
};

const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <button 
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`fixed bottom-24 right-4 z-[80] bg-zinc-800/80 backdrop-blur-md hover:bg-zinc-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0 pointer-events-none'} border border-zinc-700`}
    >
      <ArrowUp size={20} />
    </button>
  );
};

const FaqItem: React.FC<{ question: string, answer: string, isOpen: boolean, onClick: () => void }> = ({ question, answer, isOpen, onClick }) => (
    <div className="border-b border-zinc-800 last:border-0">
      <button 
        onClick={onClick}
        className="w-full py-5 md:py-6 flex justify-between items-center text-left focus:outline-none group active:bg-zinc-900/50 transition-colors px-2 rounded-lg"
        aria-expanded={isOpen}
      >
        <span className={`font-bold text-base md:text-lg transition ${isOpen ? 'text-green-400' : 'text-gray-300 group-hover:text-white'}`}>
          {question}
        </span>
        {isOpen ? <ChevronUp className="text-green-400 flex-shrink-0 ml-4" /> : <ChevronDown className="text-gray-500 flex-shrink-0 ml-4" />}
      </button>
      {isOpen && (
        <div className="pb-6 px-2 text-gray-400 leading-relaxed text-sm md:text-base max-w-2xl animate-fade-in-up">
          {answer}
        </div>
      )}
    </div>
);

const CopyButton: React.FC<{ text: string, label?: string }> = ({ text, label }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button 
            onClick={handleCopy}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-green-500 hover:text-green-400 transition p-2 active:scale-95 touch-manipulation"
        >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : (label || 'Copy')}
        </button>
    );
}

// --- Chat Bot Component ---
const ChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<{role: 'user' | 'model', text: string}[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatSessionRef = useRef<Chat | null>(null);

    // Initialize chat session (Client Side Fallback)
    const initClientChat = () => {
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
            chatSessionRef.current = ai.chats.create({
                model: 'gemini-2.5-flash',
                config: { 
                    systemInstruction: `You are the helpful AI assistant for Clones Direct, a premium cannabis clone provider since 2012. 
                
                Core Policies:
                1. Shipping: 
                   - USA Express: $40 per box (8 items max per box). Ships within 2 weeks of payment.
                   - International: $100 per box.
                2. Payment: 
                   - PayPal (Strict Note: "Home Services" ONLY. No mention of clones/order#/plants). 3% Fee applies.
                   - Crypto (BTC, ETH, SOL, USDC, USDT). 
                   - Card: Available via Card2Crypto interface in checkout. 5% Fee applies.
                3. Discounts: 
                   - Bulk: $75/clone when ordering 3+ total clones.
                   - Crypto: 50% OFF the subtotal (excluding shipping) when paying with CRYPTO.
                   - Promo Codes: We sometimes release codes like "OG" for special pricing ($80 singles / $60 bulk).
                4. Guarantee: 7-day healthy plant guarantee. No interrogation, we trust our customers.
                5. Rooting: All clones fully rooted in Oasis cubes. Not fresh cuts.
                6. Packing: Hard plastic shells, discreet boxes, climate protection.
                7. Bonuses: Free overstock clone included with every order (random selection).
                8. Environment: IPM sprays quarterly, closed loop environment. Plant health is priority #1.
                9. Max Order: 8 cuts per shipping box. Orders > 8 incur extra shipping costs per box.
                10. Legal: All clones are hemp-derived and compliant with Federal Law and the Agriculture Improvement Act (<0.3% THC).
                
                Tone: Knowledgeable, professional, helpful, grower-to-grower. Keep answers concise.` 
                }
            });
        } catch (error) {
            console.error("Failed to initialize AI (Client Side)", error);
        }
    }

    useEffect(() => {
        initClientChat();
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(scrollToBottom, [messages, isLoading]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;
        
        const userMsg = input;
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setIsLoading(true);

        try {
            // STRATEGY: 
            // 1. Try to use the Netlify Serverless Function (Secure/Production)
            // 2. If that fails (e.g. 404 in preview), fallback to Client-Side SDK (Insecure/Development)

            const serverResponse = await fetch('/.netlify/functions/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: userMsg, history: messages })
            });

            if (serverResponse.ok) {
                // Server-side success
                const data = await serverResponse.json();
                setMessages(prev => [...prev, { role: 'model', text: data.text }]);
            } else {
                // If server returns 404 (common in local preview) or 500, throw to catch block for fallback
                throw new Error(`Server returned ${serverResponse.status}`);
            }

        } catch (serverError) {
            console.warn("Secure backend unavailable, falling back to client-side key:", serverError);
            
            // --- CLIENT SIDE FALLBACK (FOR PREVIEW ONLY) ---
            try {
                if (!chatSessionRef.current) {
                   initClientChat();
                }

                if (!chatSessionRef.current) throw new Error("Chat not initialized");

                // Note: Client side uses streaming for better UX, server uses text for simplicity
                const result = await chatSessionRef.current.sendMessageStream({ message: userMsg });
                
                setMessages(prev => [...prev, { role: 'model', text: '' }]); // Placeholder
                let fullText = "";
                
                for await (const chunk of result) {
                    const c = chunk as GenerateContentResponse;
                    const text = c.text;
                    if (text) {
                        fullText += text;
                        setMessages(prev => {
                            const newArr = [...prev];
                            newArr[newArr.length - 1].text = fullText;
                            return newArr;
                        });
                    }
                }
            } catch (clientError) {
                console.error(clientError);
                setMessages(prev => [...prev, { role: 'model', text: "I'm having trouble connecting right now. Please try again later." }]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-4 z-[90] bg-green-500 hover:bg-green-400 text-black p-4 rounded-full shadow-2xl transition hover:scale-105 active:scale-95 border-2 border-black"
                aria-label="Open Chat"
            >
                {isOpen ? <X size={24} strokeWidth={3}/> : <MessageCircle size={24} strokeWidth={3}/>}
            </button>

            {isOpen && (
                // Centered on mobile via left/right anchoring instead of transform
                <div className="fixed bottom-[max(5rem,env(safe-area-inset-bottom))] left-4 right-4 md:left-4 md:right-auto md:w-96 z-[90] bg-zinc-950/95 backdrop-blur-xl border border-zinc-800 rounded-2xl shadow-2xl flex flex-col max-h-[60vh] overflow-hidden animate-fade-in-up">
                    <div className="p-4 border-b border-zinc-800 bg-zinc-900/50 flex justify-between items-center">
                        <div>
                            <h3 className="font-bold text-white">Grow Assistant</h3>
                            <p className="text-xs text-green-400 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span> Online</p>
                        </div>
                        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white md:hidden"><X size={20}/></button>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-black/30">
                        {messages.length === 0 && (
                            <div className="text-center text-gray-500 text-sm mt-4">
                                <p>Hi! I can help with shipping, payment, or strain info. Ask me anything!</p>
                            </div>
                        )}
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up`}>
                                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-base md:text-sm leading-relaxed shadow-sm ${
                                    msg.role === 'user' 
                                        ? 'bg-green-500 text-black font-bold' 
                                        : 'bg-zinc-800 text-gray-200'
                                }`}>
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                        {isLoading && messages.length > 0 && messages[messages.length -1].text === '' && (
                            <div className="flex justify-start">
                                <div className="bg-zinc-800 rounded-2xl px-4 py-2 text-gray-400 text-xs animate-pulse flex items-center gap-1">
                                    <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0s'}}></span>
                                    <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></span>
                                    <span className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                placeholder="Ask about our strains..."
                                className="flex-1 bg-black border border-zinc-800 rounded-full px-4 py-2 text-base md:text-sm text-white focus:outline-none focus:border-green-500 transition-colors"
                            />
                            <button 
                                onClick={handleSend}
                                disabled={!input.trim() || isLoading}
                                className="bg-green-500 text-black p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-400 transition transform hover:scale-105 active:scale-95"
                            >
                                <Send size={18} strokeWidth={2.5}/>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// --- Sub-Components ---

const Header: React.FC<{ 
    cartItemCount: number, 
    cartOpen: boolean, 
    setCartOpen: (open: boolean) => void,
    setCheckoutStep: (step: number) => void,
    visible: boolean,
    onNavigate: (page: 'home' | 'privacy' | 'terms') => void,
    currentPage: string
}> = ({ cartItemCount, cartOpen, setCartOpen, setCheckoutStep, visible, onNavigate, currentPage }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    // Close mobile menu when cart opens
    useEffect(() => {
        if (cartOpen) setMobileMenuOpen(false);
    }, [cartOpen]);

    const handleNavClick = (id: string) => {
        setMobileMenuOpen(false);
        if (currentPage !== 'home') {
            onNavigate('home');
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } else {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    return (
        <header className={`fixed w-full bg-black/80 backdrop-blur-xl z-50 border-b border-zinc-800/50 top-0 h-16 md:h-20 transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full relative">
                <div className="flex justify-between items-center h-full">
                <div className="flex items-center cursor-pointer group" onClick={() => onNavigate('home')}>
                    <div className="group-hover:opacity-80 transition-opacity duration-300">
                    <Logo />
                    </div>
                </div>

                <nav className="hidden md:flex items-center space-x-10">
                    <button onClick={() => handleNavClick('genetics')} className="text-sm font-bold text-gray-300 hover:text-green-400 uppercase tracking-widest transition py-2 relative">
                        Genetics
                        <span className="absolute -top-1 -right-4 bg-green-500 text-black text-[9px] font-black px-1.5 py-0.5 rounded">NEW</span>
                    </button>
                    <button onClick={() => handleNavClick('reviews')} className="text-sm font-bold text-gray-300 hover:text-green-400 uppercase tracking-widest transition py-2">Reviews</button>
                    <button onClick={() => handleNavClick('process')} className="text-sm font-bold text-gray-300 hover:text-green-400 uppercase tracking-widest transition py-2">Process</button>
                    
                    <button 
                    onClick={() => { setCartOpen(!cartOpen); setCheckoutStep(1); }}
                    className="relative bg-white hover:bg-gray-200 text-black px-6 py-2.5 rounded-full font-extrabold transition flex items-center space-x-2 active:scale-95 shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
                    >
                    <ShoppingCart className={`w-4 h-4 transition-transform ${cartItemCount > 0 ? 'scale-110' : ''}`} />
                    <span>CART</span>
                    {cartItemCount > 0 && (
                        <span className="ml-2 bg-green-500 text-black text-xs h-5 w-5 rounded-full flex items-center justify-center font-bold animate-pulse">
                        {cartItemCount}
                        </span>
                    )}
                    </button>
                </nav>

                <div className="flex items-center space-x-6 md:hidden">
                    <button 
                    onClick={() => { setCartOpen(!cartOpen); setCheckoutStep(1); }}
                    className="relative text-white p-2 active:scale-90 transition-transform"
                    aria-label="Open Cart"
                    >
                    <ShoppingCart className="w-6 h-6" />
                    {cartItemCount > 0 && (
                        <span className="absolute top-0 right-0 bg-green-500 text-black text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold border border-black">
                        {cartItemCount}
                        </span>
                    )}
                    </button>
                    <button 
                    className="text-white p-2 active:scale-90 transition-transform"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    aria-label="Open Menu"
                    >
                    {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
                </div>
            </div>

            {/* Mobile Dropdown Menu - Positioned immediately below header */}
            {mobileMenuOpen && (
                <div className="md:hidden absolute top-full left-0 w-full bg-zinc-950 border-b border-zinc-800 shadow-2xl flex flex-col animate-fade-in-up origin-top z-40">
                    {['genetics', 'reviews', 'process', 'faq'].map((id) => (
                        <button 
                            key={id} 
                            onClick={() => handleNavClick(id)} 
                            className="text-lg font-black text-white hover:text-green-400 hover:bg-zinc-900 py-5 px-6 text-left uppercase tracking-tight transition-colors border-b border-zinc-900 last:border-0 flex justify-between items-center group"
                        >
                            {id}
                            <ChevronRight className="opacity-0 group-hover:opacity-100 transition text-green-500" size={20}/>
                        </button>
                    ))}
                    <div className="p-4 bg-zinc-900/50">
                        <button 
                        onClick={() => { setMobileMenuOpen(false); setCartOpen(true); }}
                        className="w-full text-base font-black text-black bg-green-500 hover:bg-green-400 py-4 px-6 rounded-xl active:scale-95 transition shadow-lg shadow-green-500/20"
                        >
                            VIEW CART ({cartItemCount})
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
};

const FloatingCartButton: React.FC<{
    count: number;
    visible: boolean;
    onClick: () => void;
}> = ({ count, visible, onClick }) => {
    return (
        <button 
            onClick={onClick}
            className={`fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-[80] bg-white hover:bg-gray-200 text-black p-4 rounded-full shadow-2xl transition-all duration-300 border-2 border-green-500 flex items-center justify-center transform active:scale-95 ${visible ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}
            aria-label="Open Cart"
        >
            <ShoppingCart size={24} strokeWidth={3} />
            {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-green-500 text-black text-xs h-6 w-6 rounded-full flex items-center justify-center font-bold border-2 border-black animate-pulse">
                    {count}
                </span>
            )}
        </button>
    );
};

const StrainCard: React.FC<{ strain: Strain, onAdd: (s: Strain) => void, onClick: (s: Strain) => void }> = ({ strain, onAdd, onClick }) => {
    const color = getCategoryColor(strain.category);
    return (
      <div 
        className="bg-zinc-950/80 border border-zinc-800/80 rounded-2xl overflow-hidden hover:border-green-500/40 hover:bg-zinc-900 transition duration-300 group relative cursor-pointer flex flex-col h-full touch-manipulation hover:shadow-[0_0_30px_rgba(34,197,94,0.1)]"
        onClick={() => onClick(strain)}
      >
        <div className="h-40 sm:h-48 bg-zinc-900 flex items-center justify-center relative overflow-hidden">
            {/* Strain Image */}
            <div className="absolute inset-0 overflow-hidden">
                <ImageWithSkeleton 
                    src={strain.imageUrl || PLACEHOLDER_IMG} 
                    alt={strain.name} 
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition duration-700 ease-in-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent opacity-90"></div>
            </div>

            {/* Category Icon Overlay - I/H/S */}
            <div className={`absolute bottom-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black z-10 border backdrop-blur-sm ${color.bg} ${color.text} ${color.border}`}>
                {strain.category[0]}
            </div>

            {/* Stock Badge */}
            <div className="absolute top-3 right-3 bg-green-500 text-black text-[9px] font-black px-2 py-1 rounded uppercase tracking-wide z-10 shadow-lg">
                In Stock
            </div>
          
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center backdrop-blur-[2px]">
            <span className="bg-white text-black font-extrabold px-4 py-2 rounded-full text-xs uppercase tracking-wide flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition duration-300 shadow-xl">
              <Info size={14} /> View Details
            </span>
          </div>
          
          {strain.special && (
            <div className="absolute top-3 left-3 bg-yellow-500 text-black text-[9px] font-black px-2 py-1 rounded uppercase tracking-wide z-10 shadow-lg">
              Legacy
            </div>
          )}
        </div>
        
        <div className="p-4 flex flex-col flex-grow relative z-10">
          <div className="flex-grow">
            <h3 className="text-base sm:text-lg font-extrabold text-white leading-tight transition line-clamp-2 group-hover:text-green-500 mb-1.5 min-h-[2.5em]">
              {strain.name}
            </h3>
            <p className="text-[10px] text-gray-500 font-mono mb-4 uppercase tracking-wide truncate">{strain.lineage}</p>
            
            <div className="flex justify-center items-center text-xs text-gray-400 mb-4 bg-zinc-900/50 p-2 rounded-lg border border-zinc-800/50 backdrop-blur-sm">
              <div className="text-center">
                <span className="block text-gray-500 font-bold uppercase mb-0.5 text-[9px]">Flower Time</span>
                <span className="text-white font-mono font-bold text-xs">{strain.flowerTime}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between mt-auto pt-2">
            <div>
              <p className="text-xl sm:text-2xl font-black text-white leading-none tracking-tight">$100</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wide mt-0.5">$75 bulk</p>
            </div>
            <button 
              onClick={(e) => {
                  e.stopPropagation();
                  onAdd(strain);
              }}
              className="bg-green-500 hover:bg-green-400 text-black p-3 rounded-full transition shadow-lg shadow-green-500/20 hover:scale-105 active:scale-95 touch-manipulation group/btn relative overflow-hidden"
              aria-label={`Add ${strain.name} to cart`}
            >
              <Plus className="w-5 h-5 group-hover/btn:rotate-90 transition-transform duration-300 relative z-10" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>
    );
};

const StrainListItem: React.FC<{ strain: Strain, onAdd: (s: Strain) => void, onClick: (s: Strain) => void }> = ({ strain, onAdd, onClick }) => {
    const color = getCategoryColor(strain.category);
    return (
        <button 
            className="w-full bg-zinc-950/80 border border-zinc-800 rounded-xl p-3 flex items-center gap-4 hover:border-green-500/30 hover:bg-zinc-900/50 transition group text-left touch-manipulation active:bg-zinc-900 relative overflow-hidden"
            onClick={() => onClick(strain)}
        >
            {/* List View - Replaced Image with H/I/S Icon */}
            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-xl flex-shrink-0 ${color.bg} ${color.text} border ${color.border}`}>
                {strain.category[0]}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-black text-white transition group-hover:text-green-500 truncate">
                        {strain.name}
                    </h3>
                    {strain.special && <span className="bg-yellow-500 text-black text-[9px] font-black px-1.5 py-0.5 rounded uppercase flex-shrink-0">Legacy</span>}
                </div>
                <p className="text-xs text-gray-500 font-mono truncate mb-1">{strain.lineage}</p>
                <div className="flex gap-3 text-[10px] text-gray-400">
                    <span className="flex items-center gap-1"><Clock size={10}/> {strain.flowerTime}</span>
                    <span className="flex items-center gap-1 text-green-500"><Check size={10}/> In Stock</span>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right">
                    <p className="text-lg font-black text-white leading-none">$100</p>
                    <p className="text-xs text-gray-500 font-bold uppercase">$75 bulk</p>
                </div>
                <div 
                    onClick={(e) => {
                        e.stopPropagation();
                        onAdd(strain);
                    }}
                    className="bg-green-500 hover:bg-green-400 text-black p-2 rounded-full transition cursor-pointer active:scale-90"
                >
                    <Plus className="w-5 h-5" strokeWidth={3} />
                </div>
            </div>
        </button>
    );
};

// Extracted form component to maintain focus stability
// Added id and name props to ensure browser autofill works correctly without stealing focus
const InputField = ({ label, id, name, ...props }: any) => (
    <div className="space-y-1.5">
        <label htmlFor={id} className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1 block">{label}</label>
        <input 
            id={id}
            name={name}
            {...props} 
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3.5 text-base text-white placeholder-zinc-700 focus:ring-2 focus:ring-green-500/50 focus:border-green-500 outline-none transition"
        />
    </div>
);

const ShippingForm: React.FC<{
    shippingMethod: ShippingMethod;
    setShippingMethod: (method: ShippingMethod) => void;
    shippingAddress: any;
    setShippingAddress: (addr: any) => void;
    shippingBoxes: number;
    formErrors: string[];
}> = ({ shippingMethod, setShippingMethod, shippingAddress, setShippingAddress, shippingBoxes, formErrors }) => {
    
    return (
        <form className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 checkout-scroll-container pb-32" onSubmit={(e) => e.preventDefault()}>
            <div>
                <h3 className="text-xl font-black text-white border-b border-zinc-800 pb-3 mb-4">1. Shipping Method</h3>
                <div className="grid grid-cols-1 gap-3">
                    <button 
                        type="button"
                        onClick={() => setShippingMethod('domestic')}
                        className={`p-4 rounded-xl border text-sm font-bold transition flex flex-col items-start text-left relative overflow-hidden ${shippingMethod === 'domestic' ? 'bg-green-500/10 border-green-500 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.1)]' : 'bg-zinc-900 border-zinc-800 text-gray-400 hover:bg-zinc-800'}`}
                    >
                        <div className="flex justify-between w-full relative z-10">
                            <span className={`font-black mb-1 ${shippingMethod === 'domestic' ? 'text-white' : 'text-gray-300'}`}>USA Express</span>
                            {shippingMethod === 'domestic' && <Check size={16}/>}
                        </div>
                        <span className="text-xs font-medium text-gray-500 relative z-10">$40 base (+$40 per 8 items)</span>
                        {shippingMethod === 'domestic' && <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-transparent"></div>}
                    </button>
                    
                    <button 
                        type="button"
                        onClick={() => setShippingMethod('international')}
                        className={`p-4 rounded-xl border text-sm font-bold transition flex flex-col items-start text-left relative overflow-hidden ${shippingMethod === 'international' ? 'bg-green-500/10 border-green-500 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.1)]' : 'bg-zinc-900 border-zinc-800 text-gray-400 hover:bg-zinc-800'}`}
                    >
                         <div className="flex justify-between w-full relative z-10">
                            <span className={`font-black mb-1 ${shippingMethod === 'international' ? 'text-white' : 'text-gray-300'}`}>International Priority</span>
                             {shippingMethod === 'international' && <Check size={16}/>}
                        </div>
                        <span className="text-xs font-medium text-gray-500 relative z-10">$100 base (+$100 per 8 items)</span>
                         {shippingMethod === 'international' && <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-transparent"></div>}
                    </button>
                </div>
                {shippingBoxes > 1 && (
                     <p className="text-xs text-orange-400 mt-2 flex items-center gap-2"><Info size={12}/> Large order: {shippingBoxes} shipping boxes required.</p>
                )}
            </div>

            <div>
                <h3 className="text-xl font-black text-white border-b border-zinc-800 pb-3 mb-4">2. Contact Info</h3>
                
                {formErrors.length > 0 && (
                     <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-xl p-3 animate-fade-in-up">
                        <p className="text-red-400 text-xs font-bold mb-1 flex items-center gap-2"><AlertCircle size={14}/> Please fix the following:</p>
                        <ul className="list-disc list-inside text-xs text-red-300">
                            {formErrors.map((err, i) => <li key={i}>{err}</li>)}
                        </ul>
                     </div>
                )}

                <div className="space-y-6">
                    <InputField type="email" inputMode="email" autoComplete="email" label="Email Address" id="email" name="email" value={shippingAddress.email || ''} placeholder="Email Address" onChange={(e: any) => setShippingAddress({...shippingAddress, email: e.target.value})} required />
                    
                    <InputField type="text" autoComplete="name" label="Full Name" id="fullname" name="fullname" value={shippingAddress.name || ''} placeholder="Full Name" onChange={(e: any) => setShippingAddress({...shippingAddress, name: e.target.value})} required />
                    
                    <div className="space-y-6 pt-2">
                        <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <MapPin size={12}/> Shipping Address
                        </div>
                        <InputField type="text" autoComplete="address-line1" label="Address Line 1" id="address" name="address" value={shippingAddress.line1 || ''} placeholder="Address Line 1" onChange={(e: any) => setShippingAddress({...shippingAddress, line1: e.target.value})} required />
                        <InputField type="text" autoComplete="address-level2" label="City / Town" id="city" name="city" value={shippingAddress.city || ''} placeholder="City / Town" onChange={(e: any) => setShippingAddress({...shippingAddress, city: e.target.value})} required />
                        
                        <div className="grid grid-cols-2 gap-4">
                            <InputField type="text" autoComplete="address-level1" label="State/Province" id="state" name="state" value={shippingAddress.state || ''} placeholder="State" onChange={(e: any) => setShippingAddress({...shippingAddress, state: e.target.value})} required />
                            {shippingMethod === 'international' ? (
                                <InputField type="text" autoComplete="country-name" label="Country" id="country" name="country" value={shippingAddress.country || ''} placeholder="Country" onChange={(e: any) => setShippingAddress({...shippingAddress, country: e.target.value})} required />
                            ) : (
                                <InputField type="text" inputMode="numeric" autoComplete="postal-code" label="Zip Code" id="zip" name="zip" value={shippingAddress.zip || ''} placeholder="Zip Code" onChange={(e: any) => setShippingAddress({...shippingAddress, zip: e.target.value})} required />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};

// --- Main App Component ---

export default function App() {
  const [showShippingToast, setShowShippingToast] = useState(true); 
  const [viewingStrain, setViewingStrain] = useState<Strain | null>(null); 
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('Alphabetical'); 
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lastAddedItem, setLastAddedItem] = useState<string | null>(null);
  const [headerVisible, setHeaderVisible] = useState(true);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [card2CryptoLoading, setCard2CryptoLoading] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  
  // Promo Code State
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [promoError, setPromoError] = useState('');

  // New Page State
  const [currentPage, setCurrentPage] = useState<'home' | 'privacy' | 'terms'>('home');
  
  // Checkout State
  const [cryptoPaymentTab, setCryptoPaymentTab] = useState<'card' | 'manual'>('card');
  const [cartOpen, setCartOpen] = useState(false);
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod>('domestic');
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [shippingAddress, setShippingAddress] = useState<any>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [selectedPaymentType, setSelectedPaymentType] = useState<'paypal' | 'crypto' | null>(null);
  const [orderId, setOrderId] = useState<string>('');
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  
  // Lock body scroll when cart or modal is open
  useLockBodyScroll(cartOpen || !!viewingStrain);

  // Set default view mode based on screen width
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== 'undefined') {
        return window.innerWidth < 768 ? 'list' : 'grid';
    }
    return 'grid';
  });
  
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  
  const [subscribed, setSubscribed] = useState(false);
  const [cryptoRates, setCryptoRates] = useState<{bitcoin: number, ethereum: number, solana: number} | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      console.error('Failed to save cart:', error);
    }
  }, [cart]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Handle Scroll for Sticky Header hiding
  useEffect(() => {
    const handleScroll = () => {
        const geneticsSection = document.getElementById('genetics');
        if (!geneticsSection) return;
        const rect = geneticsSection.getBoundingClientRect();
        
        // Hide header ONLY when we are 'inside' the genetics section
        const isInsideMenu = rect.top < 100 && rect.bottom > 100;

        if (isInsideMenu) {
            setHeaderVisible(false);
        } else {
            setHeaderVisible(true);
        }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll to top when checkout step changes
  useEffect(() => {
    const scrollContainer = document.querySelector('.checkout-scroll-container');
    if (scrollContainer) {
        scrollContainer.scrollTop = 0;
    }
  }, [checkoutStep]);

  // Fetch Crypto Rates when checkout step reaches payment
  useEffect(() => {
      if (checkoutStep === 3 && selectedPaymentType === 'crypto' && !cryptoRates) {
          fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana&vs_currencies=usd')
              .then(res => res.json())
              .then(data => {
                  setCryptoRates({
                      bitcoin: data.bitcoin.usd,
                      ethereum: data.ethereum.usd,
                      solana: data.solana.usd
                  });
              })
              .catch(e => {
                  console.error("Crypto Fetch Error", e);
                  // Retry once after 2 seconds if failed
                  setTimeout(() => {
                      fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana&vs_currencies=usd')
                      .then(res => res.json())
                      .then(data => {
                           setCryptoRates({
                              bitcoin: data.bitcoin.usd,
                              ethereum: data.ethereum.usd,
                              solana: data.solana.usd
                           });
                      }).catch(e => console.log("Retry failed", e));
                  }, 2000);
              });
      }
  }, [checkoutStep, selectedPaymentType]);

  // Clean up toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Pricing Logic
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const isBulk = cartItemCount >= 3;

  // PROMO LOGIC: 'og' gives pricing $80/ea (single) or $60/ea (bulk)
  const unitPrice = useMemo(() => {
    if (appliedPromoCode === 'og') {
        return isBulk ? 60 : 80;
    }
    return isBulk ? 75 : 100;
  }, [isBulk, appliedPromoCode]);

  // Gross Subtotal (before any discounts, assuming base price)
  const grossCartSubtotal = cart.reduce((sum, item) => sum + (100 * item.quantity), 0);
  
  // Actual Subtotal (after bulk discount applied)
  const cartSubtotal = cart.reduce((sum, item) => sum + (unitPrice * item.quantity), 0);
  
  // Discount Amount (Diff between Gross and Effective)
  const bulkDiscountAmount = grossCartSubtotal - cartSubtotal;
  
  // Shipping Calculation
  const shippingBoxes = Math.ceil(Math.max(1, cartItemCount) / 8);
  const baseShippingRate = shippingMethod === 'domestic' ? 40 : 100;
  const shippingCost = shippingBoxes * baseShippingRate;

  // Crypto Discount Logic
  const cryptoDiscount = selectedPaymentType === 'crypto' ? cartSubtotal * 0.5 : 0;
  
  // Base Total before Fees
  const baseTotal = cartSubtotal - cryptoDiscount + shippingCost;

  // Fee Calculation
  let paymentFee = 0;
  let feeLabel = '';

  // Requirement: 3% fee for PayPal
  if (selectedPaymentType === 'paypal') {
      paymentFee = baseTotal * 0.03;
      feeLabel = 'PayPal Processing Fee (3%)';
  } 
  // Requirement: 5% fee ONLY for CardWithCrypto (Card2Crypto)
  // Requirement: NO fee for manual crypto
  else if (selectedPaymentType === 'crypto' && cryptoPaymentTab === 'card') {
      paymentFee = baseTotal * 0.05;
      feeLabel = 'Card Processing Fee (5%)';
  }

  const cartTotal = baseTotal + paymentFee;

  const handleApplyPromo = () => {
      if (promoCodeInput.trim().toLowerCase() === 'og') {
          setAppliedPromoCode('og');
          setPromoError('');
          setPromoCodeInput('');
      } else {
          setPromoError('Invalid code');
          setAppliedPromoCode(null);
      }
  };

  const removePromo = () => {
      setAppliedPromoCode(null);
      setPromoCodeInput('');
      setPromoError('');
  };

  const addToCart = (strain: Strain) => {
    const existing = cart.find(item => item.id === strain.id);
    if (existing) {
      setCart(cart.map(item => item.id === strain.id ? { ...item, quantity: item.quantity + 1 } : item));
    } else {
      setCart([...cart, { ...strain, quantity: 1 }]);
    }
    
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    setLastAddedItem(strain.name);
    
    toastTimeoutRef.current = setTimeout(() => {
        setLastAddedItem(null);
        toastTimeoutRef.current = null;
    }, 5000);
    
    setCheckoutStep(1);
  };

  const updateQuantity = (id: number, delta: number) => {
    setCart(cart.map(item => {
      if (item.id === id) {
        const newQty = Math.max(0, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const validateShipping = () => {
    const errors = [];
    if (!shippingAddress.email || !shippingAddress.email.includes('@')) errors.push("Valid email required");
    if (!shippingAddress.name) errors.push("Full name required");
    if (!shippingAddress.line1) errors.push("Address required");
    if (!shippingAddress.city) errors.push("City required");
    if (!shippingAddress.state) errors.push("State required");
    if (!shippingAddress.zip && shippingMethod === 'domestic') errors.push("Zip code required");
    if (!shippingAddress.country && shippingMethod === 'international') errors.push("Country required");
    
    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleSubscribe = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Explicitly construct URLSearchParams from FormData for better compatibility
    const data = new URLSearchParams();
    formData.forEach((value, key) => data.append(key, value as string));
    
    fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
    })
    .then(() => setSubscribed(true))
    .catch((error) => console.error(error));
  };

  // --- CARD2CRYPTO API INTEGRATION ---
  const generateCardPayment = async () => {
    setCard2CryptoLoading(true);
    try {
        const callbackUrl = encodeURIComponent(window.location.origin + `/?order=${orderId || 'checkout'}`);
        const walletRes = await fetch(`https://api.card2crypto.org/control/wallet.php?address=${POLYGON_WALLET_ADDRESS}&callback=${callbackUrl}`);
        const walletData = await walletRes.json();
        
        if (walletData.address_in) {
            // Updated to use cartTotal which now includes the fee
            const paymentUrl = `https://pay.card2crypto.org/pay.php?address=${encodeURIComponent(walletData.address_in)}&amount=${cartTotal.toFixed(2)}&email=${encodeURIComponent(shippingAddress.email)}&currency=USD&domain=pay.card2crypto.org`;
            window.open(paymentUrl, '_blank');
        } else {
            alert("Could not generate payment link. Please try manual transfer.");
        }
    } catch (e) {
        console.error("Card2Crypto Error", e);
        alert("Payment gateway connection error. Please use manual crypto transfer.");
    } finally {
        setCard2CryptoLoading(false);
    }
  };

  // --- SUBMIT ORDER TO NETLIFY AND SEND EMAIL ---
  const submitOrder = async () => {
    if (isSubmittingOrder) return;
    setIsSubmittingOrder(true);

    const newOrderId = `CD-${Math.floor(1000 + Math.random() * 9000)}`;
    setOrderId(newOrderId);

    // Prepare data for Netlify
    const cartSummary = cart.map(i => `${i.quantity}x ${i.name} ($${(unitPrice * i.quantity).toFixed(2)})`).join('\n');
    const addressString = `${shippingAddress.name}\n${shippingAddress.line1}\n${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}\n${shippingAddress.country || ''}`;
    
    const params = new URLSearchParams();
    params.append('form-name', 'orders');
    params.append('order_id', newOrderId);
    params.append('customer_email', shippingAddress.email);
    params.append('customer_name', shippingAddress.name);
    params.append('shipping_details', addressString);
    params.append('cart_summary', cartSummary);
    params.append('total_amount', cartTotal.toFixed(2));
    params.append('payment_method', selectedPaymentType || 'unknown');
    if (appliedPromoCode) {
        params.append('promo_code', appliedPromoCode);
    }

    // Generate Discount HTML for Email - Matches HTML Table Structure
    let discountHtml = '';
    if (bulkDiscountAmount > 0) {
        const label = appliedPromoCode ? `Promo Discount (${appliedPromoCode})` : 'Bulk Discount';
        discountHtml += `<tr><td style="width: 60%;"></td><td style="font-size:14px; color:#9ca3af; padding:6px 8px 6px 0;">${label}</td><td style="padding:6px; white-space:nowrap;">-$${bulkDiscountAmount.toFixed(2)}</td></tr>`;
    }
    if (cryptoDiscount > 0) {
        discountHtml += `<tr><td style="width: 60%;"></td><td style="font-size:14px; color:#22c55e; padding:6px 8px 6px 0;">Crypto Discount</td><td style="padding:6px; white-space:nowrap; color:#22c55e;">-$${cryptoDiscount.toFixed(2)}</td></tr>`;
    }
    if (paymentFee > 0) {
        discountHtml += `<tr><td style="width: 60%;"></td><td style="font-size:14px; color:#9ca3af; padding:6px 8px 6px 0;">${feeLabel}</td><td style="padding:6px; white-space:nowrap;">+$${paymentFee.toFixed(2)}</td></tr>`;
    }

    // Prepare data for EmailJS (Customer Email) - Matches HTML Template Variables
    const emailParams = {
        order_id: newOrderId,
        email: shippingAddress.email,
        subtotal: grossCartSubtotal.toFixed(2), // Matches {{subtotal}}
        shipping: shippingCost.toFixed(2), // Matches {{shipping}}
        total_price: cartTotal.toFixed(2), // Matches {{total_price}}
        // Add status header for email
        status_header: "Order Received", // Matches {{status_header}}
        status_body: "We have received your order. We will verify payment and send a shipping update shortly.", // Matches {{status_body}}
        orders: cart.map(item => ({
            name: item.name,
            units: item.quantity,
            price: (unitPrice * item.quantity).toFixed(2)
        })), // Matches {{#orders}} loop
        discount_html: discountHtml // Matches {{{discount_html}}}
    };

    try {
        const netlifyPromise = fetch("/", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: params.toString(),
        });

        const emailPromise = (EMAILJS_TEMPLATE_ID !== "REPLACE_WITH_YOUR_TEMPLATE_ID" && EMAILJS_PUBLIC_KEY !== "REPLACE_WITH_YOUR_PUBLIC_KEY")
            ? sendEmail(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, emailParams, EMAILJS_PUBLIC_KEY)
            : Promise.resolve(); // Skip email if keys are not set to prevent error

        if (EMAILJS_TEMPLATE_ID === "REPLACE_WITH_YOUR_TEMPLATE_ID") {
            console.warn("EmailJS Template ID and Public Key are missing in the code. Email will not be sent.");
        }

        await Promise.all([netlifyPromise, emailPromise]);
        
        // On success, clear cart and move to confirmation
        setCheckoutStep(4);
        localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
        console.error("Order submission failed", error);
        alert("There was a connection error submitting your order. Please try again.");
    } finally {
        setIsSubmittingOrder(false);
    }
  };

  const filteredStrains = useMemo(() => {
    let result = strainsData.filter(s => {
        const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.lineage.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'All' 
            ? true 
            : selectedCategory === 'Legacy Cuts'
            ? s.special
            : s.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    if (sortBy === 'Flower Time') {
        result.sort((a, b) => parseInt(a.flowerTime) - parseInt(b.flowerTime));
    } else if (sortBy === 'Alphabetical') {
        result.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    return result;
  }, [searchQuery, selectedCategory, sortBy]);

  const renderCartDrawer = () => {
    const bulkProgress = Math.min(cartItemCount, 3);
    const itemsNeeded = 3 - bulkProgress;

    const renderCartView = () => (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 checkout-scroll-container pb-32">
            {cart.length > 0 && (
                <div className="bg-zinc-900 rounded-xl p-4 mb-4 border border-zinc-800 relative overflow-hidden shadow-inner">
                    <div className="absolute top-0 right-0 p-2 opacity-10">
                        <Leaf size={60} />
                    </div>
                    <div className="flex justify-between text-xs font-bold mb-2 relative z-10">
                        <span className={isBulk ? "text-green-500" : "text-gray-300"}>
                            {isBulk ? (appliedPromoCode ? "Bulk + Promo Pricing!" : "Bulk Pricing Unlocked!") : `Add ${itemsNeeded} more for bulk pricing!`}
                        </span>
                        <span className="text-gray-500">{Math.min(cartItemCount, 3)}/3</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden relative z-10">
                        <div 
                            className={`h-full transition-all duration-500 ease-out ${isBulk ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]' : 'bg-green-800'}`} 
                            style={{width: `${(bulkProgress/3)*100}%`}}
                        ></div>
                    </div>
                </div>
            )}

            {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center pb-20">
                    <div className="w-24 h-24 bg-zinc-900 rounded-full flex items-center justify-center mb-6 border border-zinc-800">
                        <ShoppingCart className="w-10 h-10 text-zinc-600" />
                    </div>
                    <p className="text-white text-xl font-bold mb-2">Your cart is empty</p>
                    <p className="text-gray-500 max-w-xs">The best genetics go fast. Don't miss out on the drop.</p>
                    <button 
                        onClick={() => {
                            setCartOpen(false);
                            const el = document.getElementById('genetics');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }} 
                        className="mt-8 text-green-500 font-bold hover:text-green-400 transition underline underline-offset-4 p-4"
                    >
                        Browse Strains
                    </button>
                </div>
            ) : (
                <>
                    {cart.map(item => (
                        <div key={item.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 md:p-4 flex gap-4 animate-fade-in-up hover:bg-zinc-900 transition-colors">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-black rounded-lg flex items-center justify-center flex-shrink-0 border border-zinc-800 overflow-hidden">
                                <ImageWithSkeleton src={item.imageUrl || PLACEHOLDER_IMG} alt={item.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1">
                                <div className="flex justify-between items-start mb-1">
                                    <h3 className="font-bold text-white text-lg leading-tight line-clamp-1">{item.name}</h3>
                                    <button onClick={() => updateQuantity(item.id, -item.quantity)} className="text-zinc-600 hover:text-red-500 transition p-2"><Trash2 size={18}/></button>
                                </div>
                                <p className="text-xs text-gray-500 font-mono mb-3">{item.category}</p>
                                
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-1 bg-black rounded-lg p-1 border border-zinc-800">
                                        <button onClick={() => updateQuantity(item.id, -1)} className="text-gray-400 hover:text-white p-2 active:bg-zinc-800 rounded"><Minus size={14}/></button>
                                        <span className="font-mono font-bold text-sm w-6 text-center">{item.quantity}</span>
                                        <button onClick={() => updateQuantity(item.id, 1)} className="text-gray-400 hover:text-white p-2 active:bg-zinc-800 rounded"><Plus size={14}/></button>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-white font-bold">${unitPrice * item.quantity}</p>
                                        <p className="text-xs text-gray-600">(${unitPrice}/ea)</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {/* Promo Code Section */}
                    <div className="pt-2 animate-fade-in-up">
                        <p className="text-xs font-bold text-gray-500 uppercase mb-2">Promo Code</p>
                        {appliedPromoCode ? (
                            <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 p-3 rounded-xl">
                                <div className="flex items-center gap-2">
                                    <Tag size={16} className="text-green-500"/>
                                    <span className="text-green-500 font-bold uppercase">{appliedPromoCode} Applied</span>
                                </div>
                                <button onClick={removePromo} className="text-xs text-gray-500 hover:text-white underline">Remove</button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input 
                                    type="text" 
                                    value={promoCodeInput}
                                    onChange={(e) => setPromoCodeInput(e.target.value)}
                                    placeholder="Enter code"
                                    className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-green-500 outline-none uppercase placeholder:normal-case"
                                />
                                <button 
                                    onClick={handleApplyPromo}
                                    disabled={!promoCodeInput.trim()}
                                    className="bg-zinc-800 hover:bg-zinc-700 text-white font-bold px-4 py-2 rounded-lg text-sm transition disabled:opacity-50"
                                >
                                    Apply
                                </button>
                            </div>
                        )}
                        {promoError && <p className="text-xs text-red-500 mt-1">{promoError}</p>}
                    </div>

                    {(isBulk || appliedPromoCode) && (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-3 animate-fade-in-up">
                            <Check className="text-green-500" size={16} />
                            <p className="text-green-400 text-xs font-bold">
                                {appliedPromoCode ? 'Promo discount applied' : 'Bulk discount applied'} (${unitPrice}/ea)
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const renderPaymentView = () => {
        return (
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 checkout-scroll-container pb-32">
                
                {/* Order Summary Toggle */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
                    <button 
                        onClick={() => setShowOrderSummary(!showOrderSummary)}
                        className="w-full p-4 flex justify-between items-center text-sm font-bold text-gray-300 hover:bg-zinc-800 transition"
                    >
                        <span className="flex items-center gap-2"><ShoppingCart size={16}/> {showOrderSummary ? 'Hide' : 'Show'} Order Summary</span>
                        {showOrderSummary ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                    </button>
                    
                    {showOrderSummary && (
                        <div className="p-4 border-t border-zinc-800 bg-zinc-950/50 space-y-3">
                            <div className="space-y-1">
                                <p className="text-xs font-bold text-gray-500 uppercase">Shipping To:</p>
                                <p className="text-sm text-white">{shippingAddress.name}, {shippingAddress.line1}, {shippingAddress.city}</p>
                            </div>
                            <div className="space-y-2 pt-2 border-t border-zinc-800/50">
                                <p className="text-xs font-bold text-gray-500 uppercase">Items:</p>
                                {cart.map(item => (
                                    <div key={item.id} className="flex justify-between text-sm text-gray-300">
                                        <span>{item.quantity}x {item.name}</span>
                                        <span className="font-mono">${(unitPrice * item.quantity).toFixed(0)}</span>
                                    </div>
                                ))}
                            </div>
                            {appliedPromoCode && (
                                <div className="pt-2 border-t border-zinc-800/50 text-green-500 text-xs font-bold flex justify-between">
                                    <span>Promo Code</span>
                                    <span className="uppercase">{appliedPromoCode}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <h3 className="text-xl font-black text-white border-b border-zinc-800 pb-3 mb-4">Choose Payment Method</h3>
                
                {/* Method Selector */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                    <button 
                        onClick={() => setSelectedPaymentType('crypto')}
                        className={`p-4 rounded-xl border flex flex-col items-center justify-center gap-1 transition relative overflow-hidden group text-center ${selectedPaymentType === 'crypto' ? 'bg-yellow-500/10 border-yellow-500 text-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.1)]' : 'bg-zinc-900 border-zinc-800 text-gray-400 hover:border-zinc-700'}`}
                    >
                        <div className={`absolute top-0 right-0 bg-green-500 text-black text-[9px] font-black px-2 py-0.5 rounded-bl-lg transition ${selectedPaymentType === 'crypto' ? 'opacity-100' : 'opacity-80'}`}>50% OFF</div>
                        <Shield size={24} className="group-hover:scale-110 transition-transform mb-1"/>
                        <span className="font-bold text-lg leading-none">Crypto</span>
                        <span className="text-[10px] font-medium opacity-90 leading-tight">Pay with or without Credit/Debit Card</span>
                    </button>
                    <button 
                        onClick={() => setSelectedPaymentType('paypal')}
                        className={`p-4 rounded-xl border flex flex-col items-center justify-center gap-2 transition group ${selectedPaymentType === 'paypal' ? 'bg-blue-500/10 border-blue-500 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.1)]' : 'bg-zinc-900 border-zinc-800 text-gray-400 hover:border-zinc-700'}`}
                    >
                        <Award size={24} className="group-hover:scale-110 transition-transform"/>
                        <span className="font-bold">PayPal</span>
                    </button>
                </div>

                {/* Crypto Instructions */}
                {selectedPaymentType === 'crypto' && (
                    <div className="animate-fade-in-up space-y-4">
                        <div className="bg-green-900/10 border border-green-500/30 p-4 rounded-xl flex items-center gap-3">
                            <div className="p-2 bg-green-500 rounded-full text-black"><Check size={16}/></div>
                            <div>
                                <h4 className="font-bold text-green-500 text-sm">50% Discount Applied!</h4>
                                <p className="text-xs text-gray-300">Total reduced by ${cryptoDiscount.toFixed(2)}. Shipping excluded.</p>
                            </div>
                        </div>

                        {/* Payment Type Toggles */}
                        <div className="flex bg-zinc-900 p-1 rounded-xl">
                            <button 
                                onClick={() => setCryptoPaymentTab('card')}
                                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${cryptoPaymentTab === 'card' ? 'bg-white text-black shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
                            >
                                <CreditCard size={14}/> Pay with Card (Instant)
                            </button>
                            <button 
                                onClick={() => setCryptoPaymentTab('manual')}
                                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${cryptoPaymentTab === 'manual' ? 'bg-white text-black shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
                            >
                                <Wallet size={14}/> Manual Transfer
                            </button>
                        </div>

                        {cryptoPaymentTab === 'card' ? (
                             <div className="animate-fade-in-up">
                                <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl text-center">
                                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 text-black shadow-lg">
                                        <Zap size={32} strokeWidth={2}/>
                                    </div>
                                    <h4 className="text-white font-bold text-lg mb-2">Instant Card Payment</h4>
                                    <p className="text-gray-400 text-sm mb-6 leading-relaxed max-w-xs mx-auto">
                                        Pay with Visa, Mastercard, or Apple Pay. <br/>
                                        The transaction settles as USDC. <span className="text-green-500 font-bold">50% discount</span> applied.
                                    </p>
                                    
                                    <div className="bg-zinc-800 rounded-lg p-3 mb-6 text-xs text-gray-400">
                                         <div className="flex justify-between mb-1">
                                             <span>Discounted Subtotal + Shipping:</span>
                                             <span>${baseTotal.toFixed(2)}</span>
                                         </div>
                                         <div className="flex justify-between text-white font-bold">
                                             <span>+ Card Processing Fee (5%):</span>
                                             <span>${paymentFee.toFixed(2)}</span>
                                         </div>
                                    </div>

                                    <button 
                                        onClick={generateCardPayment}
                                        disabled={card2CryptoLoading}
                                        className="w-full bg-white text-black font-black py-4 rounded-xl uppercase tracking-wide hover:bg-gray-200 transition active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {card2CryptoLoading ? <RefreshCw className="animate-spin" size={20}/> : <ExternalLink size={20}/>}
                                        {card2CryptoLoading ? 'Generating Link...' : `Pay $${cartTotal.toFixed(2)} Now`}
                                    </button>
                                    
                                    <p className="text-[10px] text-gray-500 mt-4 flex items-center justify-center gap-1">
                                        <Shield size={10}/> Secure payment via Card2Crypto
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="animate-fade-in-up space-y-4">
                                <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-xl">
                                    <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                                        <Info size={14} className="text-blue-400"/> New to Crypto? How to pay:
                                    </h4>
                                    <ol className="list-decimal list-inside text-xs text-gray-400 space-y-2 mb-4">
                                        <li>Create an account on <strong className="text-white">MoonPay</strong> or <strong className="text-white">Coinbase</strong> below.</li>
                                        <li>Buy <strong className="text-white">Bitcoin (BTC)</strong>, <strong className="text-white">Ethereum (ETH)</strong>, or <strong className="text-white">Solana (SOL)</strong> with your card.</li>
                                        <li>Select <strong className="text-white">"Send"</strong> in your wallet app.</li>
                                        <li>Paste our address (copy below) and send the total amount.</li>
                                    </ol>
                                    <div className="grid grid-cols-2 gap-3">
                                        <a href="https://www.moonpay.com/buy" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-white text-black font-bold py-3 rounded-lg text-xs hover:bg-gray-200 transition">
                                            Open MoonPay <ExternalLink size={12}/>
                                        </a>
                                        <a href="https://www.coinbase.com/" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-[#0052FF] text-white font-bold py-3 rounded-lg text-xs hover:bg-[#0040c9] transition">
                                            Open Coinbase <ExternalLink size={12}/>
                                        </a>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {/* BTC */}
                                    <div className="bg-zinc-900 p-4 rounded-xl border border-zinc-800 hover:border-zinc-700 transition relative overflow-hidden">
                                        <div className="flex justify-between items-center mb-2 relative z-10">
                                            <span className="font-bold text-white flex items-center gap-2 text-sm md:text-base"><span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"></span> Bitcoin (BTC)</span>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-400">Amount to send:</p>
                                                <p className="font-black text-white">{cryptoRates ? (cartTotal / cryptoRates.bitcoin).toFixed(8) : '...'} BTC</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <p className="flex-1 font-mono text-[10px] md:text-xs text-gray-400 break-all bg-black p-3 rounded border border-zinc-800 select-all">3EH93C5sdFQHsQay5aw37QTrd8Pz6Sp7z1</p>
                                            <CopyButton text="3EH93C5sdFQHsQay5aw37QTrd8Pz6Sp7z1" />
                                        </div>
                                        <p className="text-[10px] text-red-400 font-bold flex items-center gap-1"><AlertTriangle size={12}/> Bitcoin Network ONLY.</p>
                                    </div>

                                    {/* ETH */}
                                    <div className="bg-zinc-900 p-4 rounded-xl border border-zinc-800 hover:border-zinc-700 transition">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="font-bold text-white flex items-center gap-2 text-sm md:text-base"><span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0"></span> Ethereum (ETH)</span>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-400">Amount to send:</p>
                                                <p className="font-black text-white">{cryptoRates ? (cartTotal / cryptoRates.ethereum).toFixed(6) : '...'} ETH</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <p className="flex-1 font-mono text-[10px] md:text-xs text-gray-400 break-all bg-black p-3 rounded border border-zinc-800 select-all">0xF397ca7792a814e777a24dEb78D74E6403483bA0</p>
                                            <CopyButton text="0xF397ca7792a814e777a24dEb78D74E6403483bA0" />
                                        </div>
                                        <p className="text-[10px] text-gray-500 leading-relaxed">Accepted: <strong className="text-white">ETH, USDC, USDT</strong>. (ERC-20)</p>
                                    </div>

                                    {/* SOL */}
                                    <div className="bg-zinc-900 p-4 rounded-xl border border-zinc-800 hover:border-zinc-700 transition">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="font-bold text-white flex items-center gap-2 text-sm md:text-base"><span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span> Solana (SOL)</span>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-400">Amount to send:</p>
                                                <p className="font-black text-white">{cryptoRates ? (cartTotal / cryptoRates.solana).toFixed(4) : '...'} SOL</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <p className="flex-1 font-mono text-[10px] md:text-xs text-gray-400 break-all bg-black p-3 rounded border border-zinc-800 select-all">B1XJsAf5U7kHZaxjwsPkcKacoC1Apv2uZu2oZqUsdhSV</p>
                                            <CopyButton text="B1XJsAf5U7kHZaxjwsPkcKacoC1Apv2uZu2oZqUsdhSV" />
                                        </div>
                                        <p className="text-[10px] text-gray-500 leading-relaxed">Accepted: <strong className="text-white">SOL, USDC, USDT</strong>. (Solana Network)</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* PayPal Instructions */}
                {selectedPaymentType === 'paypal' && (
                    <div className="animate-fade-in-up space-y-6">
                         <div className="bg-red-500/10 border border-red-500 p-4 rounded-xl relative overflow-hidden">
                            <h4 className="font-black text-red-500 mb-2 text-sm uppercase flex items-center gap-2"><AlertTriangle size={16}/> CRITICAL INSTRUCTION</h4>
                            <p className="text-sm text-white leading-relaxed font-bold">
                                ONLY input "Home Services" as the payment note.
                            </p>
                            <p className="text-xs text-red-400 mt-2 font-bold leading-relaxed">
                                Do NOT mention clones, order #, plants, or genetics. This protects both of us.
                            </p>
                        </div>

                        <div className="bg-blue-900/10 border border-blue-500/30 p-6 rounded-xl text-center">
                            <p className="text-gray-400 text-sm mb-2">Total Amount Due (includes 3% fee):</p>
                            <p className="text-white text-2xl font-black mb-4">${cartTotal.toFixed(2)}</p>
                            
                            <a 
                                href="https://www.paypal.me/cdservices1" 
                                target="_blank" 
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 bg-[#0070BA] hover:bg-[#005ea6] text-white font-bold py-4 px-8 rounded-full transition mb-6 shadow-lg shadow-blue-500/20 active:scale-95"
                            >
                                Pay with PayPal <ExternalLink size={16}/>
                            </a>
                            
                            <div className="border-t border-blue-500/20 pt-4 text-left">
                                <p className="text-xs text-blue-300 font-bold mb-2">Trouble with the link? Send manually:</p>
                                <div className="flex items-center gap-2 bg-black/50 p-2 rounded-lg border border-blue-500/20">
                                    <div className="w-8 h-8 rounded-full bg-[#0070BA] flex items-center justify-center text-white font-bold text-xs">P</div>
                                    <div className="flex-1">
                                        <p className="text-[10px] text-gray-400">PayPal Username</p>
                                        <p className="text-sm font-mono text-white">cdservices1</p>
                                    </div>
                                    <CopyButton text="cdservices1" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderOrderConfirmationView = () => (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 animate-fade-in-up checkout-scroll-container pb-32">
            <div className="text-center py-6">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black shadow-lg shadow-green-500/30 animate-pulse">
                    <Check size={40} strokeWidth={3}/>
                </div>
                <h2 className="text-2xl font-black text-white mb-2">Order Received</h2>
                <div className="bg-zinc-800 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-700">
                    <p className="text-white font-mono font-bold text-sm">Order ID: #{orderId}</p>
                    <CopyButton text={orderId} label=" " />
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-xl mt-6 text-left max-w-sm mx-auto">
                    <h4 className="text-green-500 font-bold text-sm mb-2 flex items-center gap-2"><Info size={16}/> Next Steps</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        1. If you haven't paid yet, please complete your <strong>{selectedPaymentType === 'crypto' ? 'Crypto' : 'PayPal'}</strong> payment now.
                        <br/><br/>
                        2. We will verify funds and email you a shipping update shortly.
                    </p>
                </div>
            </div>

            <div className="bg-white text-black p-6 rounded-xl font-mono text-sm relative overflow-hidden shadow-2xl border-t-4 border-green-500">
                <div className="border-b-2 border-dashed border-black pb-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <span className="font-bold text-lg">CLONES DIRECT</span>
                        <span className="text-xs">RECEIPT</span>
                    </div>
                    <p className="text-xs">{new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
                    <p className="text-xs font-bold mt-1">Order #{orderId}</p>
                </div>
                
                <div className="space-y-2 mb-4">
                    {cart.map(item => (
                        <div key={item.id} className="flex justify-between">
                            <span>{item.quantity}x {item.name}</span>
                            <span>${(unitPrice * item.quantity).toFixed(2)}</span>
                        </div>
                    ))}
                </div>

                <div className="border-t border-black pt-2 space-y-1 text-right">
                     <div className="flex justify-between">
                        <span>Subtotal (Gross)</span>
                        <span>${grossCartSubtotal.toFixed(2)}</span>
                    </div>
                    {bulkDiscountAmount > 0 && (
                        <div className="flex justify-between text-zinc-600 font-bold">
                            <span>{appliedPromoCode ? 'Promo' : 'Bulk'} Discount</span>
                            <span>-${bulkDiscountAmount.toFixed(2)}</span>
                        </div>
                    )}
                    {cryptoDiscount > 0 && (
                        <div className="flex justify-between text-green-700 font-bold">
                            <span>Crypto Discount (50%)</span>
                            <span>-${cryptoDiscount.toFixed(2)}</span>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <span>Shipping ({shippingMethod})</span>
                        <span>${shippingCost}</span>
                    </div>
                    {paymentFee > 0 && (
                        <div className="flex justify-between">
                            <span>{feeLabel}</span>
                            <span>${paymentFee.toFixed(2)}</span>
                        </div>
                    )}
                    <div className="flex justify-between text-lg font-bold border-t-2 border-black pt-2 mt-2">
                        <span>TOTAL PAID</span>
                        <span>${cartTotal.toFixed(2)}</span>
                    </div>
                </div>

                <div className="mt-6 border-t-2 border-dashed border-black pt-4">
                     <p className="font-bold mb-1">Shipping To:</p>
                     <p>{shippingAddress.name}</p>
                     <p>{shippingAddress.line1}</p>
                     <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}</p>
                     {shippingAddress.country && <p>{shippingAddress.country}</p>}
                     <p>{shippingAddress.email}</p>
                </div>
            </div>

            <div className="bg-zinc-900 rounded-lg p-4 text-xs text-gray-500 text-center">
                A confirmation email will be sent to <strong>{shippingAddress.email}</strong> once we verify funds. Please reference <strong>#{orderId}</strong> if you contact us.
            </div>
        </div>
    );

    const renderContent = () => {
        if (cart.length === 0 && checkoutStep === 1) return renderCartView();
        switch (checkoutStep) {
            case 1: return renderCartView();
            case 2: return (
                <ShippingForm 
                    shippingMethod={shippingMethod}
                    setShippingMethod={setShippingMethod}
                    shippingAddress={shippingAddress}
                    setShippingAddress={setShippingAddress}
                    shippingBoxes={shippingBoxes}
                    formErrors={formErrors}
                />
            );
            case 3: return renderPaymentView();
            case 4: return renderOrderConfirmationView();
            default: return renderCartView();
        }
    };

    const stepNames = ['Cart', 'Shipping', 'Payment', 'Confirm'];

    return (
        <div className="fixed inset-0 z-[100] overflow-hidden">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity" onClick={() => setCartOpen(false)} />
            <div className="absolute right-0 top-0 h-[100dvh] w-full max-w-md bg-zinc-950 shadow-2xl flex flex-col border-l border-zinc-800 transform transition-transform duration-300">
                
                <div className="p-4 md:p-6 border-b border-zinc-800 flex justify-between items-center bg-zinc-950 flex-shrink-0 pt-[max(1.5rem,env(safe-area-inset-top))]">
                    <h2 className="text-xl md:text-2xl font-black text-white flex items-center gap-3">
                        {stepNames[checkoutStep - 1].toUpperCase()}
                    </h2>
                    <button onClick={() => setCartOpen(false)} className="text-gray-400 hover:text-white p-2 hover:bg-zinc-800 rounded-full transition">
                        <X size={24} />
                    </button>
                </div>

                {checkoutStep < 4 && (
                    <div className="px-6 py-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-900/50 flex-shrink-0">
                        {stepNames.slice(0, 3).map((name, index) => (
                            <div key={index} className="flex flex-col items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm transition-all duration-300 ${
                                    index + 1 === checkoutStep 
                                        ? 'bg-green-500 text-black scale-110 shadow-lg shadow-green-500/20' 
                                        : index + 1 < checkoutStep
                                        ? 'bg-green-800 text-white' 
                                        : 'bg-zinc-800 text-gray-400'
                                }`}>
                                    {index + 1 < checkoutStep ? <Check size={16}/> : index + 1}
                                </div>
                                <span className={`text-[10px] uppercase font-bold mt-1 transition-colors ${index + 1 <= checkoutStep ? 'text-white' : 'text-gray-600'}`}>{name}</span>
                            </div>
                        ))}
                    </div>
                )}

                {renderContent()}

                {cart.length > 0 && checkoutStep < 4 && (
                    <div className="p-4 border-t border-zinc-800 bg-zinc-950/95 backdrop-blur-md flex-shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-5px_20px_rgba(0,0,0,0.5)] relative z-20">
                         
                        {/* Collapsible Price Breakdown - Now Absolute to overlay content instead of pushing layout */}
                        {showPriceBreakdown && (
                             <div className="absolute bottom-full left-0 w-full bg-zinc-950 border-t border-zinc-800 p-4 animate-fade-in-up space-y-2 shadow-2xl">
                                <div className="flex justify-between items-center text-sm text-gray-400">
                                    <span>Subtotal</span>
                                    <span>${grossCartSubtotal.toFixed(2)}</span>
                                </div>
                                {bulkDiscountAmount > 0 && (
                                    <div className="flex justify-between items-center text-sm text-green-500 font-bold">
                                        <span>{appliedPromoCode ? 'Promo' : 'Bulk'} Discount</span>
                                        <span>-${bulkDiscountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {cryptoDiscount > 0 && (
                                    <div className="flex justify-between items-center text-sm text-yellow-500 font-bold">
                                        <span>Crypto Discount (50%)</span>
                                        <span>-${cryptoDiscount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center text-sm text-gray-400">
                                    <span>Shipping</span>
                                    <span>${shippingCost}</span>
                                </div>
                                {paymentFee > 0 && (
                                    <div className="flex justify-between items-center text-sm text-gray-300 font-bold">
                                        <span>{feeLabel}</span>
                                        <span>+${paymentFee.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Footer Main Row */}
                        <div className="flex items-center gap-4">
                            {/* Total Section - Toggle Breakdown */}
                            <button 
                                onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                                className="flex flex-col items-start min-w-[80px] group"
                            >
                                <div className="flex items-center gap-1 text-gray-500 text-[10px] font-bold uppercase tracking-wider group-hover:text-white transition">
                                    Total {showPriceBreakdown ? <ChevronDown size={12}/> : <ChevronUp size={12}/>}
                                </div>
                                <div className="text-xl font-black text-white leading-none group-hover:text-green-400 transition">${cartTotal.toFixed(2)}</div>
                            </button>

                            {/* Buttons */}
                            <div className="flex-1 flex gap-2">
                                {checkoutStep > 1 && (
                                    <button
                                        onClick={() => setCheckoutStep(step => step - 1)}
                                        className="p-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition flex items-center justify-center active:scale-95"
                                        aria-label="Back"
                                    >
                                        <ChevronLeft size={20}/>
                                    </button>
                                )}
                                
                                <button 
                                    onClick={() => {
                                        if (checkoutStep === 2) {
                                            if (validateShipping()) {
                                                setCheckoutStep(3);
                                            }
                                        } else if (checkoutStep === 3) {
                                            submitOrder();
                                        } else {
                                            setCheckoutStep(step => Math.min(3, step + 1));
                                        }
                                    }}
                                    disabled={(checkoutStep === 3 && !selectedPaymentType) || isSubmittingOrder}
                                    className={`flex-1 bg-green-500 hover:bg-green-400 text-black font-black uppercase tracking-wide py-3 px-4 rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 shadow-lg shadow-green-500/20 text-sm md:text-base group`}
                                >
                                    {isSubmittingOrder ? (
                                        <><RefreshCw className="animate-spin" size={18}/> Processing...</>
                                    ) : (
                                        <>
                                            {checkoutStep === 1 && (<><Check size={18} className="group-hover:scale-110 transition"/> Checkout</>)}
                                            {checkoutStep === 2 && (<>Continue <ChevronRight size={18} className="group-hover:translate-x-1 transition"/></>)}
                                            {checkoutStep === 3 && (<>I Sent Payment <ChevronRight size={18} className="group-hover:translate-x-1 transition"/></>)}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {checkoutStep === 4 && (
                     <div className="p-4 md:p-6 border-t border-zinc-800 bg-zinc-950 flex-shrink-0 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                         <button 
                             onClick={() => {
                                 setCart([]);
                                 setCheckoutStep(1);
                                 setCartOpen(false);
                             }}
                             className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-4 rounded-xl shadow-lg"
                         >
                             Close Receipt
                         </button>
                     </div>
                )}
            </div>
        </div>
    );
  };

  const renderStrainModal = (strain: Strain, onClose: () => void) => {
    const color = getCategoryColor(strain.category);
    
    const strainDetails = [
        { label: "Lineage", value: strain.lineage, icon: <Leaf size={16}/> },
        { label: "Category", value: strain.category, icon: <Globe size={16}/> },
        { label: "Flower Time", value: strain.flowerTime, icon: <Clock size={16}/> },
        { label: "Terpenes", value: strain.terps, icon: <Star size={16}/> },
        { label: "Breeder", value: strain.breeder || "Clones Direct Exclusive", icon: <Award size={16}/> },
        { label: "Stock", value: strain.stock, icon: <Package size={16}/> },
    ];

    return (
        <div className="fixed inset-0 z-[110] overflow-hidden md:overflow-y-auto">
            <div className="absolute inset-0 bg-black/95 backdrop-blur-md flex items-end md:items-center justify-center md:p-4 animate-fade-in-up" onClick={onClose}>
                {/* 
                    Mobile Layout Strategy: 
                    - h-[95dvh] max-height ensures it takes almost full mobile viewport
                    - rounded-t-3xl gives it a bottom-sheet feel on mobile.
                */}
                <div 
                    className="bg-zinc-950 border-t md:border border-zinc-800 rounded-t-3xl md:rounded-3xl w-full max-w-4xl max-h-[92dvh] md:max-h-[90vh] shadow-2xl relative flex flex-col md:block overflow-hidden" 
                    onClick={e => e.stopPropagation()}
                >
                    
                    <button onClick={onClose} className="absolute top-4 right-4 text-white p-2 bg-black/50 hover:bg-black rounded-full z-20 transition backdrop-blur-sm active:scale-95 hover:rotate-90 duration-300">
                        <X size={24} />
                    </button>

                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto md:overflow-visible">
                        <div className="flex flex-col md:grid md:grid-cols-2 h-full md:h-auto">
                            {/* Image Side - Smaller on mobile */}
                            <div className="h-64 md:h-full bg-zinc-900 relative shrink-0">
                                <ImageWithSkeleton src={strain.imageUrl || PLACEHOLDER_IMG} alt={strain.name} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent md:bg-gradient-to-r"></div>
                                
                                {strain.special && (
                                    <div className="absolute bottom-4 left-4 md:right-auto bg-yellow-500 text-black text-sm font-black px-4 py-2 rounded-full uppercase tracking-wide z-10 shadow-lg flex items-center gap-2">
                                        <Award size={16}/> Legacy Cut
                                    </div>
                                )}
                            </div>

                            {/* Details Side */}
                            <div className="p-6 md:p-10 flex flex-col justify-center">
                                <h2 className="text-3xl md:text-5xl font-black text-white mb-2 leading-tight tracking-tight">{strain.name}</h2>
                                <p className={`text-sm font-bold uppercase tracking-wide mb-6 ${color.text} flex items-center gap-2`}>
                                    <span className={`w-2 h-2 rounded-full ${color.bg.replace('/10', '')} bg-current`}></span>
                                    {strain.category}
                                </p>
                                
                                {/* Description */}
                                {strain.description && (
                                    <p className="text-gray-300 text-base leading-relaxed mb-8 border-l-2 border-green-500/50 pl-4 font-medium">
                                        {strain.description}
                                    </p>
                                )}

                                <div className="space-y-4 border-t border-b border-zinc-800 py-6 mb-8 bg-zinc-900/30 rounded-xl px-4">
                                    {strainDetails.map((detail, index) => (
                                        <div key={index} className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-2 text-gray-500 font-bold">
                                                {detail.icon} {detail.label}
                                            </span>
                                            <span className="text-white font-mono font-bold text-right">{detail.value}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Desktop Footer (Hidden on Mobile) */}
                                <div className="hidden md:flex flex-col sm:flex-row gap-4 justify-between items-center mt-auto">
                                    <div className="text-left w-full sm:w-auto">
                                        <p className="text-4xl font-black text-white tracking-tighter">$100</p>
                                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wide">($75/ea for 3+)</p>
                                    </div>
                                    <button 
                                        onClick={() => addToCart(strain)}
                                        className="w-full sm:w-auto bg-green-500 hover:bg-green-400 text-black font-black uppercase tracking-wide px-8 py-4 rounded-xl transition shadow-lg shadow-green-500/20 flex items-center justify-center gap-2 hover:scale-105 active:scale-95"
                                    >
                                        <Plus size={18} strokeWidth={3}/> Add to Cart
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Footer (Sticky at bottom, hidden on Desktop) */}
                    <div className="md:hidden p-4 border-t border-zinc-800 bg-zinc-950 flex justify-between items-center gap-4 shrink-0 z-10 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-2xl">
                        <div>
                            <p className="text-2xl font-black text-white tracking-tighter">$100</p>
                            <p className="text-xs text-gray-500 font-bold uppercase tracking-wide">($75/ea for 3+)</p>
                        </div>
                        <button 
                            onClick={() => addToCart(strain)}
                            className="flex-1 bg-green-500 hover:bg-green-400 text-black font-black uppercase tracking-wide px-6 py-3 rounded-xl transition shadow-lg shadow-green-500/20 flex items-center justify-center gap-2 active:scale-95"
                        >
                            <Plus size={18} strokeWidth={3}/> Add to Cart
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-green-500 selection:text-black pb-12">
      
      <Header 
        cartItemCount={cartItemCount} 
        cartOpen={cartOpen} 
        setCartOpen={setCartOpen} 
        setCheckoutStep={setCheckoutStep} 
        visible={headerVisible}
        onNavigate={setCurrentPage}
        currentPage={currentPage}
      />
      <ScrollToTop />
      
      {/* Floating Cart Button (shows when header hides) */}
      <FloatingCartButton 
         count={cartItemCount} 
         visible={!headerVisible} 
         onClick={() => { setCartOpen(true); setCheckoutStep(1); }}
      />

      {/* Item Added Toast - Enhanced Layout */}
      {lastAddedItem && (
        <div 
            className="fixed top-24 right-4 z-[120] w-[calc(100vw-2rem)] md:w-80 bg-zinc-900/90 border border-green-500/30 p-4 rounded-2xl shadow-2xl animate-fade-in-up backdrop-blur-md"
            onMouseEnter={() => { if(toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current); }}
            onMouseLeave={() => { 
                toastTimeoutRef.current = setTimeout(() => {
                    setLastAddedItem(null);
                    toastTimeoutRef.current = null;
                }, 2000);
            }}
        >
          <div className="flex items-start gap-3 mb-3">
             <div className="bg-green-500 text-black p-1.5 rounded-full mt-0.5 shadow-lg shadow-green-500/20"><Check size={14} strokeWidth={3}/></div>
             <div className="flex-1">
               <h4 className="font-bold text-white text-sm leading-none mb-1">Added to Cart</h4>
               <p className="text-xs text-gray-400 line-clamp-1">{lastAddedItem}</p>
             </div>
             <button onClick={() => setLastAddedItem(null)} className="text-gray-500 hover:text-white"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
             <button 
                onClick={() => {
                    setLastAddedItem(null);
                    setViewingStrain(null);
                }} 
                className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-gray-300 transition"
             >
                Add More
             </button>
             <button 
                onClick={() => { 
                    setLastAddedItem(null); 
                    setCartOpen(true); 
                    setViewingStrain(null);
                }} 
                className="px-3 py-2 rounded-lg bg-green-500 hover:bg-green-400 text-xs font-bold text-black transition flex items-center justify-center gap-1 hover:brightness-110 shadow-lg shadow-green-500/10"
             >
                View Cart <ArrowRight size={12}/>
             </button>
          </div>
        </div>
      )}

      {viewingStrain && renderStrainModal(viewingStrain, () => setViewingStrain(null))}
      {cartOpen && renderCartDrawer()}

      {/* Chat Bot */}
      <ChatWidget />

      {/* Main Content Area */}
      {currentPage === 'home' ? (
        <>
            <section className="pt-32 md:pt-40 pb-20 md:pb-32 px-4 sm:px-6 lg:px-8 bg-black relative overflow-hidden">
                {/* Ambient Background */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-green-500/20 rounded-full blur-[120px] pointer-events-none mix-blend-screen animate-pulse" style={{animationDuration: '10s'}}></div>
                <div className="absolute top-20 left-0 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[100px] pointer-events-none mix-blend-screen"></div>
                
                {/* Limited Offer Notification */}
                {showShippingToast && (
                    <div className="absolute top-20 left-4 right-4 md:left-auto md:top-24 md:right-8 z-30 md:w-80 animate-fade-in-up origin-top">
                        <div className="bg-zinc-900/80 border border-yellow-500/30 rounded-xl p-4 shadow-2xl backdrop-blur-md relative overflow-hidden group">
                            <div className="absolute inset-0 bg-yellow-500/5 group-hover:bg-yellow-500/10 transition"></div>
                            <div className="relative z-10 flex gap-3">
                                <div className="bg-yellow-500 text-black p-2 rounded-lg flex-shrink-0 h-fit shadow-lg shadow-yellow-500/20">
                                    <Zap className="w-5 h-5" strokeWidth={3} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <h4 className="text-white font-black text-sm mb-1 uppercase tracking-wide">
                                            Limited Offer
                                        </h4>
                                        <button onClick={() => setShowShippingToast(false)} className="text-gray-500 hover:text-white transition -mt-1 -mr-1 p-1">
                                            <X size={14} />
                                        </button>
                                    </div>
                                
                                    <p className="text-gray-300 text-xs leading-relaxed font-bold">
                                        50% OFF orders paid with Crypto.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="max-w-5xl mx-auto relative z-10 text-center pt-8 md:pt-0">
                
                <div className="inline-flex items-center gap-2 bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-full px-4 py-1.5 mb-8 animate-fade-in-up hover:border-green-500/50 transition duration-300 shadow-xl">
                    <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    <span className="text-xs font-bold text-gray-300 tracking-wide uppercase">Elite Clone Provider Since 2012</span>
                </div>
                
                <h2 className="text-5xl sm:text-7xl md:text-9xl font-black mb-8 leading-[0.9] tracking-tighter text-white animate-fade-in-up mix-blend-overlay" style={{animationDelay: '0.1s'}}>
                    ROOTED.<br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-green-500 to-green-600 animate-pulse">READY.</span><br />
                    SHIPPED.
                </h2>
                
                <p className="text-lg md:text-2xl text-gray-400 mb-10 max-w-2xl mx-auto font-medium animate-fade-in-up leading-relaxed" style={{animationDelay: '0.2s'}}>
                    Trusted by commercial facilities and home growers worldwide. Verified genetics, pest-free guarantee, and discreet shipping.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
                    <button 
                    onClick={() => {
                        const el = document.getElementById('genetics');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }} 
                    className="w-full sm:w-auto bg-green-500 hover:bg-green-400 text-black font-black px-12 py-5 rounded-full transition text-lg uppercase tracking-wide shadow-[0_0_40px_rgba(34,197,94,0.3)] hover:shadow-[0_0_60px_rgba(34,197,94,0.5)] active:scale-95 hover:-translate-y-1"
                    >
                    View Menu
                    </button>
                    <a href="#process" className="text-sm font-bold text-gray-400 hover:text-white transition flex items-center gap-2 p-4 hover:bg-zinc-900 rounded-full group">
                        How it works <ChevronRight size={16} className="group-hover:translate-x-1 transition"/>
                    </a>
                </div>
                
                <div className="flex flex-wrap justify-center gap-6 md:gap-12 text-xs md:text-sm font-bold text-gray-500 animate-fade-in-up uppercase tracking-widest" style={{animationDelay: '0.4s'}}>
                    <span className="flex items-center gap-2"><Check size={18} className="text-green-500"/> Healthy Roots</span>
                    <span className="flex items-center gap-2"><Check size={18} className="text-green-500"/> Pest Free</span>
                    <span className="flex items-center gap-2"><Check size={18} className="text-green-500"/> 100% Guaranteed</span>
                </div>
                </div>
            </section>

            <section className="py-20 border-y border-zinc-900 bg-zinc-950 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:bg-zinc-900 transition duration-300 group hover:-translate-y-1">
                    <div className="p-4 bg-zinc-950 rounded-2xl group-hover:scale-110 transition duration-300 mb-4 border border-zinc-800 group-hover:border-green-500/30 shadow-xl">
                        <Shield className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-black text-white mb-2">Satisfaction Guarantee</h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                        We're not finished until you're happy. 7-day health guarantee on all plants upon arrival.
                    </p>
                    </div>
                    <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:bg-zinc-900 transition duration-300 group hover:-translate-y-1">
                    <div className="p-4 bg-zinc-950 rounded-2xl group-hover:scale-110 transition duration-300 mb-4 border border-zinc-800 group-hover:border-green-500/30 shadow-xl">
                        <Leaf className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-black text-white mb-2">Premium Rooted Clones</h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                        Fully rooted in Oasis cubes for guaranteed success. Fresh unrooted cuts available via special request.
                    </p>
                    </div>
                    <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:bg-zinc-900 transition duration-300 group hover:-translate-y-1">
                    <div className="p-4 bg-zinc-950 rounded-2xl group-hover:scale-110 transition duration-300 mb-4 border border-zinc-800 group-hover:border-green-500/30 shadow-xl">
                        <Truck className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-black text-white mb-2">Global Express Shipping</h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                        1-2 day USA delivery. 3-5 day International Priority to most countries worldwide.
                    </p>
                    </div>
                </div>
                </div>
            </section>

            <section id="genetics" className="py-20 bg-black relative scroll-mt-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
                    <div>
                    <h2 className="text-4xl md:text-6xl font-black mb-2 text-white tracking-tighter">THE MENU</h2>
                    <p className="text-gray-400 text-sm font-medium border-l-2 border-green-500 pl-3">{filteredStrains.length} verified strains available.</p>
                    </div>
                </div>
                
                {/* Sticky Filter Bar */}
                <div className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl py-4 -mx-4 px-4 md:-mx-8 md:px-8 border-b border-zinc-800 mb-8 transition-all">
                    <div className="flex flex-col md:flex-row gap-4 justify-between max-w-7xl mx-auto">
                        {/* Categories */}
                        <div className="flex overflow-x-auto pb-1 gap-2 scrollbar-hide">
                            {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`px-4 py-2 rounded-full font-bold text-xs whitespace-nowrap transition uppercase tracking-wider flex-shrink-0 border active:scale-95 ${
                                selectedCategory === cat 
                                    ? 'bg-white text-black border-white shadow-[0_0_15px_rgba(255,255,255,0.2)]' 
                                    : 'bg-zinc-900 text-gray-400 hover:bg-zinc-800 border-zinc-800'
                                }`}
                            >
                                {cat}
                            </button>
                            ))}
                        </div>

                        {/* Controls */}
                        <div className="flex gap-2 w-full md:w-auto items-center">
                            <div className="flex bg-zinc-900 rounded-lg p-0.5 border border-zinc-800 flex-shrink-0">
                                <button 
                                    onClick={() => setViewMode('grid')}
                                    className={`p-2 rounded-md transition ${viewMode === 'grid' ? 'bg-zinc-800 text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
                                    title="Grid View"
                                >
                                    <Grid size={18} />
                                </button>
                                <button 
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded-md transition ${viewMode === 'list' ? 'bg-zinc-800 text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
                                    title="List View"
                                >
                                    <Menu size={18} />
                                </button>
                            </div>

                            <select 
                                value={sortBy} 
                                onChange={(e) => setSortBy(e.target.value as SortOption)}
                                className="bg-zinc-900 border border-zinc-800 text-white text-sm font-bold rounded-lg px-3 py-2 outline-none focus:border-green-500 h-[42px] flex-shrink-0 cursor-pointer hover:bg-zinc-800 transition"
                            >
                                <option value="Alphabetical">A-Z</option>
                                <option value="Featured">Featured</option>
                                <option value="Flower Time">Fastest Flower</option>
                            </select>
                            <div className="relative flex-1 md:w-56 group">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-green-500 transition" size={16} />
                                <input 
                                type="text" 
                                placeholder="Search genetics..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none font-bold placeholder:font-medium transition text-base md:text-sm h-[42px]"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {filteredStrains.length === 0 ? (
                    <div className="text-center py-20 bg-zinc-900/30 rounded-2xl border border-zinc-800 border-dashed animate-fade-in-up">
                    <Search className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg font-bold">No strains found</p>
                    <button onClick={() => setSearchQuery('')} className="mt-4 text-green-500 font-bold hover:text-green-400">Clear Search</button>
                    </div>
                ) : (
                    <div className={viewMode === 'grid' 
                    ? "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 animate-fade-in-up" 
                    : "flex flex-col gap-3 max-w-3xl mx-auto w-full animate-fade-in-up"
                    }>
                    {filteredStrains.map((strain) => (
                        <React.Fragment key={strain.id}>
                        {viewMode === 'grid' 
                            ? <StrainCard strain={strain} onAdd={addToCart} onClick={setViewingStrain} />
                            : <StrainListItem strain={strain} onAdd={addToCart} onClick={setViewingStrain} />
                        }
                        </React.Fragment>
                    ))}
                    </div>
                )}
                </div>
            </section>

            <section id="reviews" className="py-20 bg-zinc-950 border-y border-zinc-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl md:text-5xl font-black text-white mb-12 text-center tracking-tighter">COMMUNITY FEEDBACK</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    {[
                    { initial: "M", name: "Mike T.", location: "Michigan", text: "These arrived greener than my locals. Roots were popping out of the plugs. Transplanted immediately—they didn't skip a beat." },
                    { initial: "J", name: "James R.", location: "Oklahoma", text: "Packaging is A1. Super secure holders meant the cuts didn't move an inch. Apple Fritter smelled loud even in veg." },
                    { initial: "S", name: "Sarah L.", location: "Maine", text: "Communication was on point. They held my package a day because of a freeze warning. Appreciate the care." }
                    ].map((review, idx) => (
                    <div key={idx} className="bg-zinc-900/40 p-8 rounded-2xl border border-zinc-800 hover:border-zinc-700 transition hover:bg-zinc-900 group">
                        <div className="flex gap-1 text-green-500 mb-4">
                        {[...Array(5)].map((_, i) => <Star key={i} fill="currentColor" size={16} className="drop-shadow-lg"/>)}
                        </div>
                        <p className="text-gray-300 text-sm leading-relaxed mb-6 italic">"{review.text}"</p>
                        <div className="flex items-center gap-3 border-t border-zinc-800 pt-4">
                        <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center font-black text-gray-500 text-sm group-hover:bg-green-500 group-hover:text-black transition">{review.initial}</div>
                        <div>
                            <p className="text-white font-bold text-sm">{review.name}</p>
                            <p className="text-gray-600 text-xs font-bold flex items-center gap-1"><Check size={10} className="text-green-500"/> Verified • {review.location}</p>
                        </div>
                        </div>
                    </div>
                    ))}
                </div>
                </div>
            </section>

            <section id="process" className="py-24 bg-black">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tighter">HOW IT WORKS</h2>
                    <p className="text-gray-400 text-base">Simple, secure, guaranteed.</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 relative">
                    {/* Connecting Line (Desktop) */}
                    <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-zinc-800 -translate-x-1/2"></div>
                    
                    {[
                    { num: "1", title: "Add to Cart", desc: "Browse genetics. Live inventory. Max 8 cuts per shipping box (+$40 shipping for each additional 8 cuts).", icon: <ShoppingCart size={20}/> },
                    { num: "2", title: "Pay & Schedule", desc: "PayPal or Crypto (50% OFF clones). Ships within 2 weeks depending on weather & roots.", icon: <Truck size={20}/> },
                    { num: "3", title: "Secure Packing", desc: "Professional hard plastic shells in discreet, climate-protected boxes.", icon: <Package size={20}/> },
                    { num: "4", title: "Express Delivery", desc: "USPS Express. Free bonus clone (overstock) included with every order.", icon: <Clock size={20}/> }
                    ].map((step, idx) => (
                    <div key={idx} className={`flex gap-6 p-8 bg-zinc-950 rounded-2xl border border-zinc-800 hover:border-green-500/30 transition hover:bg-zinc-900/50 group relative z-10 ${idx % 2 === 0 ? 'md:text-right md:flex-row-reverse' : ''}`}>
                        <div className="flex-shrink-0 w-12 h-12 bg-zinc-900 text-white border border-zinc-800 rounded-full flex items-center justify-center font-black text-lg group-hover:bg-green-500 group-hover:text-black group-hover:scale-110 transition duration-300 shadow-lg">
                        {step.num}
                        </div>
                        <div className="flex-1">
                        <div className={`flex items-center gap-2 mb-2 ${idx % 2 === 0 ? 'md:justify-end' : ''}`}>
                            <h3 className="text-xl font-bold text-white">{step.title}</h3>
                            <div className="text-green-500 hidden md:block">{step.icon}</div>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                        </div>
                    </div>
                    ))}
                </div>
                
                <div className="mt-16 text-center bg-zinc-900/30 p-8 rounded-3xl border border-zinc-800">
                        <h4 className="text-white font-bold mb-3 flex items-center justify-center gap-2"><Leaf size={16} className="text-green-500"/> Grower Notes</h4>
                        <p className="text-sm text-gray-500 max-w-2xl mx-auto leading-relaxed">
                            We perform quarterly IPM foliar sprays. All environments are closed loop. Plant health is our #1 priority. 
                            Our strains are grown from seed or sourced from trusted breeders and growers.
                        </p>
                    </div>
                </div>
            </section>

            <section id="faq" className="py-24 bg-zinc-950 border-t border-zinc-900">
                <div className="max-w-3xl mx-auto px-4">
                <h2 className="text-3xl font-black text-white mb-10 text-center flex items-center justify-center gap-3 tracking-tight">
                    <HelpCircle className="text-green-500"/> FAQ
                </h2>
                <div className="space-y-2">
                    <FaqItem 
                    question="When will my order ship?"
                    answer="Orders ship within 2 weeks of payment unless otherwise stated. We monitor weather and root development to ensure safe arrival."
                    isOpen={openFaq === 0}
                    onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                    />
                    <FaqItem 
                    question="What is your guarantee policy?"
                    answer="We’re not finished until you’re happy with your plants! If there’s an issue within 7 days of delivery, we’ll send replacements as soon as possible."
                    isOpen={openFaq === 1}
                    onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                    />
                    <FaqItem 
                    question="Do you ship internationally?"
                    answer="Yes, international shipping is available for $100 per box unless otherwise stated. Please check local laws before ordering."
                    isOpen={openFaq === 2}
                    onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                    />
                    <FaqItem 
                    question="What payment methods do you accept?"
                    answer="We accept PayPal and cryptocurrency (BTC, ETH, SOL, USDC, USDT). Pay with Crypto for 50% off your clones!"
                    isOpen={openFaq === 3}
                    onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                    />
                    <FaqItem 
                    question="How are the clones rooted?"
                    answer="All clones are rooted in oasis root cubes for optimal growth."
                    isOpen={openFaq === 4}
                    onClick={() => setOpenFaq(openFaq === 4 ? null : 4)}
                    />
                    <FaqItem 
                    question="Who are you?"
                    answer="We’re passionate growers supplying clones since 2012—our genetics are trusted worldwide! Strains are pheno-hunted in-house or sourced from respected breeders."
                    isOpen={openFaq === 5}
                    onClick={() => setOpenFaq(openFaq === 5 ? null : 5)}
                    />
                </div>
                </div>
            </section>

            <section className="py-20 bg-green-500 text-black">
                <div className="max-w-3xl mx-auto px-4 text-center">
                <Mail className="w-16 h-16 mx-auto mb-6" strokeWidth={2} />
                <h2 className="text-4xl md:text-6xl font-black mb-4 tracking-tighter">GET DROP ALERTS</h2>
                <p className="text-lg font-bold mb-8 max-w-lg mx-auto opacity-80">
                    New genetics announced via email. No spam, just verified drops.
                </p>
                
                {subscribed ? (
                    <div className="bg-black text-white p-8 rounded-2xl animate-fade-in-up shadow-2xl inline-block">
                        <Check size={48} className="mx-auto text-green-500 mb-4" strokeWidth={3}/>
                        <h3 className="text-2xl font-black mb-2">YOU'RE ON THE LIST.</h3>
                        <p className="text-gray-400 font-bold">Watch your inbox for the next drop.</p>
                    </div>
                ) : (
                    <form 
                        onSubmit={handleSubscribe}
                        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                    >
                        {/* Hidden input to route submission to Netlify */}
                        <input type="hidden" name="form-name" value="subscribe" />
                        {/* Honeypot field for spam prevention */}
                        <input name="bot-field" style={{ display: 'none' }} />
                        
                        <input 
                        type="email"
                        name="email"
                        placeholder="your@email.com" 
                        required
                        className="flex-1 bg-white border-2 border-black rounded-xl px-5 py-4 font-bold placeholder:text-gray-400 focus:outline-none focus:ring-4 focus:ring-black/20"
                        />
                        <button type="submit" className="bg-black text-white font-black px-8 py-4 rounded-xl uppercase tracking-wide hover:bg-zinc-900 transition whitespace-nowrap active:scale-95 shadow-xl">
                        Join List
                        </button>
                    </form>
                )}
                </div>
            </section>
        </>
      ) : (
          <LegalPage 
            title={currentPage === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
            content={currentPage === 'terms' ? <TermsContent /> : <PrivacyContent />}
            onBack={() => setCurrentPage('home')}
          />
      )}

      <footer className="bg-zinc-950 border-t border-zinc-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col items-center md:items-start gap-2">
             <div onClick={() => setCurrentPage('home')} className="cursor-pointer hover:opacity-80 transition"><Logo /></div>
             <div className="text-sm text-gray-500 mt-4 text-center md:text-left space-y-1">
                <p>&copy; 2025. All rights reserved.</p>
                <p className="text-xs text-zinc-700 max-w-md">All clones are hemp-derived and contain less than 0.3% Delta-9 THC in compliance with the Agriculture Improvement Act.</p>
             </div>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 text-xs font-bold text-gray-500 uppercase tracking-widest">
            <button onClick={() => setCurrentPage('terms')} className="hover:text-green-500 transition">Terms</button>
            <button onClick={() => setCurrentPage('privacy')} className="hover:text-green-500 transition">Privacy</button>
            <a href="mailto:clonesdirect@protonmail.com" className="hover:text-green-500 transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}